import React, { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from 'providers/GlobalProvider';
import { useLogAppAction } from 'hooks/useLogAppAction';
import SingleScreeningResults from '../tables/SingleScreeningResults';
import MultiScreeningResults from '../tables/MultiScreeningResults';
import EntityScreeningResultsTable from '../tables/entityTable/EntityScreeningResultsTable';
import {
  ENTITY_AI_SAMPLE_SIZE,
  EntityScreenInputMode,
  EntityScreenRerunReason,
  EntityScreenSubject,
} from '../tables/entityTable/entityHitsPagination';
import ProductScreeningResultsTable from '../tables/commodityTable/ProductScreeningResultsTable';
import CircularProgress from '@mui/material/CircularProgress';
import { Link } from 'react-router-dom';
import AIEvalProduct from './AIEvalProduct';
import SelectionsBadge from '../SelectionsBadge';
import { exportToCSV } from '../../Common/CSVManage';
import {
  useRunAndPersistAdverseMedia,
  useRunBeneficialOwnershipSearch,
} from 'api/bitedb/ai/ai.hooks';
import {
  CustomLabel,
  CustomSwitch,
  ModuleContentTooltip,
  moduleTooltipProps,
} from 'components/Common/MUIStyledComponents';
import CreateTicketWizard from 'components/Wizard/CreateTicketWizard';
import { Modal, Autocomplete, Button, TextField } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import DataSearchAPI from 'api/DataSearch';
import SaveFilterModal from './SaveFilterModal';
import { fetchRiskConfig, RiskConfig } from './AIEvaluate';
import { screeningCountries } from 'utils/screeningCountries';
import { normalizeExternalUrl } from 'utils/common';
import LockIcon from '@mui/icons-material/Lock';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import EntityScreeningView, {
  EntityScreenFilters,
} from '../entityScreening/EntityScreeningView';
import type { EntityTableExportContext } from '../tables/entityTable/entityTableFilterParams';

interface ISearchProps {
  inputs: any;
  setInputs: any;
  handleSubmit: any;
  results: any;
  ownershipResults?: any;
  page: number;
  setPage: any;
  type: string;
  setShowFullRecord: any;
  userHasSearch: boolean;
  cyberTab: string | undefined;
  handleTabSwitch: any | undefined;
  cyberHeaders: string[];
  getCSVResults?: any;
  handleMultiPageChange?: any;
  aiSums?: any;
  state: any;
  setEntityFilterState?: any;
  entityFilterOptions?: any;
  // Entities-tool only: the screen's lens, source country, agency and list
  // filters, set from its Filters modal.
  entityScreenFilters?: EntityScreenFilters;
  unfilteredMatchTotal?: number | null;
  entityFilterSubmit?: any;
  setEntityFilterSubmit?: any;
  filters?: any;
  setFilters?: any;
  appliedFilter?: any;
  setAppliedFilter?: any;
  setCommodityFilterState?: any;
  commodityFilterOptions?: any;
  commodityFilterSubmit?: any;
  setCommodityFilterSubmit?: any;
  listFilters?: any;
  setListFilters?: any;
  codeFilters?: any;
  setCodeFilters?: any;
  appliedListFilter?: any;
  setAppliedListFilter?: any;
  appliedCodeFilter?: any;
  setAppliedCodeFilter?: any;
  openFilters?: any;
  openFiltersCode?: any;
  handleClear?: any;
  handleFullReset?: any;
  handleOpenDate?: any;
  handleSwitchChange?: any;
  createTicket?: any;
  saving?: boolean;
  setFrequency?: any;
  frequency?: string;
  shareEmail: string[];
  setShareEmail?: any;
  ticketNameOpen: boolean;
  setTicketNameOpen: any;
  aiLoading?: boolean;
  newTicketName: string;
  setNewTicketName: any;
  isInteractionDisabled?: boolean;
  tableResetSignal?: number;
  setBeneficialOwnership?: any;
  setAdverseMedia?: any;
  // Import-tool only: selected import jurisdiction ("Trade Type") and its
  // setter, driving the country tabs shown below the product hero.
  tradeType?: string;
  setTradeType?: (value: string) => void;
  hasRunOnce?: boolean;
  // Entities-tool only: the parent owns these because its screen reads them.
  entityInputMode?: EntityScreenInputMode;
  setEntityInputMode?: (mode: EntityScreenInputMode) => void;
  entityScreenReason?: EntityScreenRerunReason;
}

type COpt =
  | string
  | {
      name?: string;
      iso2?: string;
    };

const switchInfoTooltipProps = moduleTooltipProps({
  color: 'var(--text-prim25)',
  maxWidth: '320px',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  lineHeight: 1.5,
});

const Search: React.FC<ISearchProps> = ({
  setShowFullRecord,
  type,
  setPage,
  page,
  inputs,
  setInputs,
  handleSubmit,
  results,
  ownershipResults,
  cyberTab,
  handleTabSwitch,
  cyberHeaders,
  getCSVResults,
  handleMultiPageChange,
  aiSums,
  state,
  setEntityFilterState,
  entityFilterOptions,
  entityScreenFilters,
  unfilteredMatchTotal,
  entityFilterSubmit,
  setEntityFilterSubmit,
  filters,
  setFilters,
  appliedFilter,
  setAppliedFilter,
  setCommodityFilterState,
  commodityFilterOptions,
  commodityFilterSubmit,
  setCommodityFilterSubmit,
  listFilters,
  setListFilters,
  codeFilters,
  setCodeFilters,
  appliedListFilter,
  setAppliedListFilter,
  appliedCodeFilter,
  setAppliedCodeFilter,
  openFilters,
  openFiltersCode,
  handleClear,
  handleFullReset,
  handleOpenDate,
  handleSwitchChange,
  createTicket,
  saving,
  setFrequency,
  frequency,
  shareEmail,
  setShareEmail,
  ticketNameOpen,
  setTicketNameOpen,
  aiLoading,
  newTicketName,
  setNewTicketName,
  isInteractionDisabled = false,
  tableResetSignal = 0,
  setBeneficialOwnership,
  setAdverseMedia,
  tradeType,
  setTradeType,
  hasRunOnce = false,
  entityInputMode: controlledEntityInputMode,
  setEntityInputMode: setControlledEntityInputMode,
  entityScreenReason,
}) => {
  const location = useLocation();
  const direction = location.pathname.includes('import') ? 'Import' : 'Export';
  const isEntitySearch = type === 'Entities';
  const isProductSearch = type === 'Products';
  const usesScreeningRedesign = isEntitySearch || isProductSearch;
  // The docType this search will save the ticket as — must match the values the
  // save handlers use (Entities.tsx → 'Person / Company', Commodities.tsx →
  // `${direction} Product Screen`). Drives whether CreateTicketWizard offers the
  // re-screening frequency step (these are the re-screenable types).
  const wizardDocType = isEntitySearch
    ? 'Person / Company'
    : isProductSearch
    ? `${direction} Product Screen`
    : undefined;
  // Country/jurisdiction tabs live only on the Import product tool. Each tab
  // maps a label to the OpenSearch "Trade Type" value the query scopes to.
  const isImportProduct = isProductSearch && direction === 'Import';
  const activeTradeType = tradeType || 'US Import';
  const isNonUsImport = isImportProduct && activeTradeType !== 'US Import';
  const importTradeTabs = [
    { label: 'US Imports', value: 'US Import' },
    { label: 'EU Imports', value: 'EU Import' },
    { label: 'Canada Imports', value: 'CA Import' },
  ];
  const [localEntityInputMode, setLocalEntityInputMode] =
    useState<EntityScreenInputMode>('details');
  const entityInputMode = controlledEntityInputMode ?? localEntityInputMode;
  const setEntityInputMode =
    setControlledEntityInputMode ?? setLocalEntityInputMode;
  // "How do we evaluate risk?" modal — lazily fetches the live risk-mapping config.
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const [riskConfig, setRiskConfig] = useState<RiskConfig | null>(null);
  const [riskConfigLoading, setRiskConfigLoading] = useState(false);
  const openRiskModal = async () => {
    setRiskModalOpen(true);
    if (!riskConfig && !riskConfigLoading) {
      setRiskConfigLoading(true);
      const cfg = await fetchRiskConfig();
      setRiskConfig(cfg);
      setRiskConfigLoading(false);
    }
  };
  const [entityInsightPanels, setEntityInsightPanels] = useState({
    ai: false,
    media: false,
    ownership: false,
  });
  const [entityParametersOpen, setEntityParametersOpen] = useState(false);
  const [activeEntityParameter, setActiveEntityParameter] = useState<
    'lenses' | 'countries' | 'agencies' | 'lists' | null
  >(null);
  const [entityParameterSearch, setEntityParameterSearch] = useState('');
  const [entityAdverseMedia, setEntityAdverseMedia] = useState<any>(null);
  const [entityAdverseMediaError, setEntityAdverseMediaError] = useState<
    string | null
  >(null);
  const [entityExportFileName, setEntityExportFileName] = useState('');
  const [entityExportFileNameOpen, setEntityExportFileNameOpen] =
    useState(false);
  const [entityExportContext, setEntityExportContext] =
    useState<EntityTableExportContext | null>(null);
  const [entityBeneficialOwnership, setEntityBeneficialOwnership] =
    useState<any>(null);
  const [entityBeneficialOwnershipError, setEntityBeneficialOwnershipError] =
    useState<string | null>(null);
  const [savedFiltersOpen, setSavedFiltersOpen] = useState(false);
  const [saveFilterModalOpen, setSaveFilterModalOpen] = useState(false);
  const [productParametersOpen, setProductParametersOpen] = useState(false);
  const [activeProductParameter, setActiveProductParameter] = useState<
    | 'comLenses'
    | 'countries'
    | 'agencies'
    | 'lists'
    | 'hsCodeLists'
    | 'sections'
    | 'chapters'
    | 'codes'
    | null
  >(null);
  const [productParameterSearch, setProductParameterSearch] = useState('');
  const [savedProductListFiltersOpen, setSavedProductListFiltersOpen] =
    useState(false);
  const [savedProductCodeFiltersOpen, setSavedProductCodeFiltersOpen] =
    useState(false);
  const [productSaveFilterModalOpen, setProductSaveFilterModalOpen] =
    useState(false);
  const [productSaveFilterTarget, setProductSaveFilterTarget] = useState<
    'list' | 'code'
  >('list');
  const entityParameterGridRef = useRef<HTMLDivElement | null>(null);
  const savedFiltersWrapRef = useRef<HTMLDivElement | null>(null);
  const productListParameterGridRef = useRef<HTMLDivElement | null>(null);
  const productCodeParameterGridRef = useRef<HTMLDivElement | null>(null);
  const savedProductListFiltersWrapRef = useRef<HTMLDivElement | null>(null);
  const savedProductCodeFiltersWrapRef = useRef<HTMLDivElement | null>(null);
  // Monotonic request ids for the on-demand insight checks. A check captures
  // its id, and only commits its async result if the id is still current —
  // so a request that resolves after the entity changed is discarded instead
  // of repopulating the UI with stale results for the previous entity.
  const entityInsightRequestRef = useRef({ media: 0, ownership: 0 });

  // Collapse open filter dropdowns when the user clicks anywhere outside them.
  useEffect(() => {
    if (
      !activeEntityParameter &&
      !savedFiltersOpen &&
      !activeProductParameter &&
      !savedProductListFiltersOpen &&
      !savedProductCodeFiltersOpen
    ) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (
        activeEntityParameter &&
        entityParameterGridRef.current &&
        target &&
        !entityParameterGridRef.current.contains(target)
      ) {
        setActiveEntityParameter(null);
      }
      if (
        savedFiltersOpen &&
        savedFiltersWrapRef.current &&
        target &&
        !savedFiltersWrapRef.current.contains(target)
      ) {
        setSavedFiltersOpen(false);
      }
      if (
        activeProductParameter &&
        target &&
        productListParameterGridRef.current &&
        productCodeParameterGridRef.current &&
        !productListParameterGridRef.current.contains(target) &&
        !productCodeParameterGridRef.current.contains(target)
      ) {
        setActiveProductParameter(null);
      }
      if (
        savedProductListFiltersOpen &&
        savedProductListFiltersWrapRef.current &&
        target &&
        !savedProductListFiltersWrapRef.current.contains(target)
      ) {
        setSavedProductListFiltersOpen(false);
      }
      if (
        savedProductCodeFiltersOpen &&
        savedProductCodeFiltersWrapRef.current &&
        target &&
        !savedProductCodeFiltersWrapRef.current.contains(target)
      ) {
        setSavedProductCodeFiltersOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [
    activeEntityParameter,
    savedFiltersOpen,
    activeProductParameter,
    savedProductListFiltersOpen,
    savedProductCodeFiltersOpen,
  ]);

  const { enqueueSnackbar } = useSnackbar();
  const { cur_user, isWhiteLabel } = useContext(GlobalContext);
  const isRestricted = cur_user?.role === 'domain-restricted';
  const logAppAction = useLogAppAction();
  const {
    mutateAsync: runEntityAdverseMedia,
    isPending: isEntityAdverseMediaLoading,
  } = useRunAndPersistAdverseMedia();
  const {
    mutateAsync: runEntityBeneficialOwnership,
    isPending: isEntityBeneficialOwnershipLoading,
  } = useRunBeneficialOwnershipSearch();

  const handleClearInput = (field: any) => {
    const onChangeValue = { ...inputs };
    onChangeValue[field] = '';
    setInputs(onChangeValue);
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    const onChangeValue = { ...inputs };
    onChangeValue[name] = value;
    setInputs(onChangeValue);
  };

  const handleCountryChange = (newValue: any) => {
    const onChangeValue = { ...inputs };
    onChangeValue.country = getName(newValue);
    setInputs(onChangeValue);
  };

  const handleProductCountryChange = (
    field: 'origin' | 'destination',
    newValue: any,
  ) => {
    const onChangeValue = { ...inputs };
    onChangeValue[field] = getName(newValue);
    setInputs(onChangeValue);
  };

  const handleTicketNameClose = () => {
    setTicketNameOpen(false);
    setNewTicketName('');
  };

  const runIfEnabled = (callback: () => void) => {
    if (isInteractionDisabled) {
      return;
    }
    callback();
  };

  const findCountryOption = (valueIsoOrName: string | undefined | null) => {
    if (!valueIsoOrName) {
      return null;
    }
    const v = String(valueIsoOrName).toUpperCase();
    const normalizedValue = v === 'USA' ? 'US' : v;
    return (
      (screeningCountries as COpt[]).find(
        (c) =>
          getName(c).toUpperCase() === normalizedValue ||
          getIso(c) === normalizedValue,
      ) || null
    );
  };

  const getName = (o: COpt) => (typeof o === 'string' ? o : o?.name ?? '');

  const getIso = (o: COpt) =>
    typeof o === 'string' ? '' : (o?.iso2 ?? '')?.toUpperCase();

  const displayLabel = (o: COpt) => {
    const n = getName(o);
    const code = getIso(o);
    return code ? `${n} - ${code}` : n;
  };

  const productCountryAutocompleteSx = (width?: string) => ({
    ...(width ? { width: width } : {}),
    '& .MuiOutlinedInput-root': {
      height: '44px',
      borderRadius: '8px',
      background: 'var(--Background-200, var(--bg-colors))',
      border: '1px solid var(--es-soft-border)',
      color: 'var(--text-primary-color)',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingTop: '0 !important',
      paddingBottom: '0 !important',
    },
    '& .MuiInputBase-input': {
      color: 'var(--text-primary-color)',
      fontFamily: 'Inter',
      fontSize: '14px',
    },
    '& .MuiSvgIcon-root': {
      color: 'var(--text-secondary)',
    },
    '& .MuiAutocomplete-clearIndicator': {
      display: 'none',
    },
  });

  const productCountryAutocompleteComponentsProps = {
    popper: {
      sx: {
        '& .MuiAutocomplete-option': {
          color: 'var(--text-primary-white) !important',
        },
      },
    },
    paper: {
      sx: {
        color: 'var(--text-primary-white) !important',
      },
    },
  };

  const renderProductCountryDropdown = (
    field: 'origin' | 'destination',
    placeholder: string,
    clearLabel: string,
    width?: string,
  ) => (
    <Autocomplete
      options={screeningCountries as COpt[]}
      getOptionLabel={(option: any) => displayLabel(option)}
      isOptionEqualToValue={(opt: any, val: any) =>
        (Boolean(getIso(opt)) && getIso(opt) === getIso(val)) ||
        getName(opt) === getName(val)
      }
      clearOnEscape
      componentsProps={productCountryAutocompleteComponentsProps}
      sx={productCountryAutocompleteSx(width)}
      onChange={(_: any, newValue: any) =>
        handleProductCountryChange(field, newValue)
      }
      renderInput={(params: any) => (
        <TextField
          {...params}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <PublicOutlinedIcon
                  sx={{
                    color: 'var(--text-secondary)',
                    fontSize: 20,
                    marginRight: '8px',
                  }}
                />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {inputs[field] && (
                  <span
                    className='entity-screening-input-clear'
                    role='button'
                    aria-label={clearLabel}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => runIfEnabled(() => handleClearInput(field))}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </span>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      value={findCountryOption(inputs[field])}
      disabled={isInteractionDisabled}
    />
  );

  const totalAppliedEntityParameters =
    (state?.lenses?.length || 0) +
    (state?.countries?.length || 0) +
    (state?.agencies?.length || 0) +
    (state?.lists?.length || 0);
  const totalAppliedProductParameters =
    (state?.comLenses?.length || 0) +
    (state?.countries?.length || 0) +
    (state?.agencies?.length || 0) +
    (state?.lists?.length || 0) +
    (state?.hsCodeLists?.length || 0) +
    (state?.sections?.length || 0) +
    (state?.chapters?.length || 0) +
    (state?.codes?.length || 0);
  const totalAppliedParameters = isProductSearch
    ? totalAppliedProductParameters
    : totalAppliedEntityParameters;
  const entityParameterConfigs = [
    {
      type: 'lenses',
      title: 'Lens',
      pluralLabel: 'lenses',
      placeholder: 'All lenses',
      reason: 'Lens',
      icon: <ShieldOutlinedIcon sx={{ fontSize: 18 }} />,
      options: entityFilterOptions?.lensOpts,
    },
    {
      type: 'countries',
      title: 'Listing Country',
      pluralLabel: 'listing countries',
      placeholder: 'All listing countries',
      reason: 'Listing Country',
      icon: <PublicOutlinedIcon sx={{ fontSize: 19 }} />,
      options: entityFilterOptions?.countryOpts,
    },
    {
      type: 'agencies',
      title: 'Agency',
      pluralLabel: 'agencies',
      placeholder: 'All agencies',
      reason: 'Agency',
      icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 19 }} />,
      options: entityFilterOptions?.agencyOpts,
    },
    {
      type: 'lists',
      title: 'List',
      pluralLabel: 'lists',
      placeholder: 'All lists',
      reason: 'List',
      icon: <FilterListIcon sx={{ fontSize: 18 }} />,
      options: entityFilterOptions?.listOpts,
    },
  ] as const;
  const productListParameterConfigs = [
    {
      type: 'comLenses',
      title: 'Lens',
      pluralLabel: 'lenses',
      placeholder: 'All lenses',
      reason: 'Lens',
      icon: <ShieldOutlinedIcon sx={{ fontSize: 18 }} />,
      options: commodityFilterOptions?.lensOpts,
    },
    {
      type: 'countries',
      title: 'Listing Country',
      pluralLabel: 'listing countries',
      placeholder: 'All listing countries',
      reason: 'Country Regulator',
      icon: <PublicOutlinedIcon sx={{ fontSize: 19 }} />,
      options: commodityFilterOptions?.countryOpts,
    },
    {
      type: 'agencies',
      title: 'Agency',
      pluralLabel: 'agencies',
      placeholder: 'All agencies',
      reason: 'Agency',
      icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 19 }} />,
      options: commodityFilterOptions?.agencyOpts,
    },
    {
      type: 'lists',
      title: 'List',
      pluralLabel: 'lists',
      placeholder: 'All lists',
      reason: 'List',
      icon: <FilterListIcon sx={{ fontSize: 18 }} />,
      options: commodityFilterOptions?.listOpts,
    },
  ] as const;
  const productCodeParameterConfigs = [
    {
      type: 'hsCodeLists',
      title: 'HTS Code List',
      pluralLabel: 'HTS code lists',
      placeholder: 'All HTS code lists',
      reason: 'HS Code List',
      icon: <BookmarkBorderOutlinedIcon sx={{ fontSize: 18 }} />,
      options: commodityFilterOptions?.hsCodeListOpts,
    },
    {
      type: 'sections',
      title: 'Section',
      pluralLabel: 'sections',
      placeholder: 'All sections',
      reason: 'Section',
      icon: <ShieldOutlinedIcon sx={{ fontSize: 18 }} />,
      options: commodityFilterOptions?.sectionOpts,
    },
    {
      type: 'chapters',
      title: 'Chapter',
      pluralLabel: 'chapters',
      placeholder: 'All chapters',
      reason: 'Chapter',
      icon: <ArticleOutlinedIcon sx={{ fontSize: 18 }} />,
      options: commodityFilterOptions?.chapterOpts,
    },
    {
      type: 'codes',
      title: 'HTS Code',
      pluralLabel: 'HTS codes',
      placeholder: 'All HTS codes',
      reason: 'HS Code',
      icon: <FilterListIcon sx={{ fontSize: 18 }} />,
      options: commodityFilterOptions?.codeOpts,
    },
  ] as const;
  const screeningResultsTotal =
    results?.data?.[0]?.result?.body?.hits?.total?.value || 0;
  const entityResultsTotal = screeningResultsTotal;
  // How many rows actually came back on the (capped) full-set fetch — used to
  // report truncation accurately regardless of the fetch cap.
  const loadedEntityCount =
    results?.data?.[0]?.result?.body?.hits?.hits?.length || 0;
  const productHasQuery = Boolean(inputs.hsCode || inputs.desc);
  const canRunProductScreen = !isInteractionDisabled && productHasQuery;
  // Only the active input mode's value should drive actions. The inactive
  // field is also cleared on mode switch, but guarding here prevents a stale
  // hidden value from exporting/saving/running insight checks.
  const getActiveEntityQuery = () =>
    entityInputMode === 'record' ? inputs.record : inputs.primary;
  const hasEntityQuery = Boolean(getActiveEntityQuery());
  // A screen has completed (returned a response) — true even when it returned
  // zero matches, which is what lets a "no hits" screen be saved.
  const hasCompletedScreen =
    Boolean(results?.data?.length) && !results?.isLoading;
  const latestAiSummary = aiSums?.[0];
  // Strip wrapping quotes the LLM sometimes adds around the entity name in
  // its rationale ("'Acme Corp' was flagged..."). Replaces both straight and
  // curly quote pairs around the actual searched name only.
  const stripEntityNameQuotes = (
    text: string | undefined,
    entityName: string | undefined,
  ): string => {
    if (!text) return '';
    const name = (entityName || '').trim();
    if (!name) return text;
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return text.replace(
      new RegExp(`['"“‘’”\`]\\s*(${escaped})\\s*['"“‘’”\`]`, 'gi'),
      '$1',
    );
  };
  const sanitizedExplanation = stripEntityNameQuotes(
    latestAiSummary?.explanation,
    latestAiSummary?.entity_name || inputs.primary,
  );
  const latestAiTopMatches = Array.isArray(latestAiSummary?.top_matches)
    ? latestAiSummary.top_matches
        .map((match: any) =>
          typeof match === 'string' ? match : match?.name || match?.id,
        )
        .filter(Boolean)
    : [];
  const entityRiskLabel = latestAiSummary?.risk || 'N/A';
  const entityRiskText = String(entityRiskLabel).toLowerCase();
  // Only map to a color when the level is explicit. Errors, "Unknown", and the
  // initial "N/A" must NOT fall through to a (misleading) medium/amber default.
  const entityRiskClass = entityRiskText.includes('low')
    ? 'low'
    : entityRiskText.includes('high')
    ? 'high'
    : entityRiskText.includes('medium')
    ? 'medium'
    : 'unknown';
  // Country-risk inline notes (CR-004 data gap, CR-005 jurisdiction driver).
  const entityCountryRisk = latestAiSummary?.country_risk;
  const entityCountryDataGap = Boolean(entityCountryRisk?.unknown);
  const entityCountryDrove =
    latestAiSummary?.rating_driver === 'country_tier' ||
    latestAiSummary?.rating_driver === 'sanctioned_override';
  const entityCountryDriverNote = !entityCountryDrove
    ? ''
    : latestAiSummary?.rating_driver === 'sanctioned_override'
    ? `Driven by sanctioned/embargoed jurisdiction${
        entityCountryRisk?.iso ? ` (${entityCountryRisk.iso})` : ''
      }`
    : `Driven by jurisdiction risk${
        entityCountryRisk?.iso ? `: ${entityCountryRisk.iso}` : ''
      }${
        entityCountryRisk?.category ? ` — ${entityCountryRisk.category}` : ''
      }`;
  const isCheckingEntityAdverseMedia = isEntityAdverseMediaLoading;
  const isCheckingEntityBeneficialOwnership =
    isEntityBeneficialOwnershipLoading;
  const getEntityParameterLabel = (
    type: 'lenses' | 'countries' | 'agencies' | 'lists',
    placeholder: string,
  ) => {
    const selections = state?.[type] || [];
    if (!selections.length) {
      return placeholder;
    }
    if (selections.length === 1) {
      return selections[0];
    }
    return `${selections.length} selected`;
  };
  const getEntityParameterOptions = (config: any) => {
    const rawOptions = config?.options?.data || [];
    // Options are flattened from nested aggregation buckets, so the same
    // key (e.g. "United States") can appear once per parent bucket. Dedupe
    // by the trimmed key before filtering/rendering. Selected values that no
    // longer appear in the bucket data (e.g. agencies auto-selected from a
    // lens) are folded back in so the user can always uncheck them.
    const seen = new Set<string>();
    const deduped: any[] = [];
    rawOptions.forEach((option: any) => {
      const value = String(option?.key ?? option).trim();
      if (!value || seen.has(value)) {
        return;
      }
      seen.add(value);
      deduped.push(option);
    });
    const currentSelections: string[] = state?.[config.type] || [];
    currentSelections.forEach((value: string) => {
      const trimmed = String(value).trim();
      if (!trimmed || seen.has(trimmed)) {
        return;
      }
      seen.add(trimmed);
      deduped.push({ key: trimmed });
    });
    const query = entityParameterSearch.toLowerCase();
    const filtered = query
      ? deduped.filter((option: any) =>
          String(option?.key || option)
            .toLowerCase()
            .includes(query),
        )
      : deduped;
    // Surface selected values at the top so users can always see (and remove)
    // them, even when the unselected list is long.
    const selectedSet = new Set(currentSelections);
    const selectedFirst = [
      ...filtered.filter((option: any) =>
        selectedSet.has(String(option?.key || option)),
      ),
      ...filtered.filter(
        (option: any) => !selectedSet.has(String(option?.key || option)),
      ),
    ];
    return selectedFirst;
  };
  const handleEntityParameterToggle: any = (
    type: 'lenses' | 'countries' | 'agencies' | 'lists',
  ) => {
    if (isInteractionDisabled) {
      return;
    }
    setEntityParameterSearch('');
    setSavedFiltersOpen(false);
    setActiveEntityParameter((prev) => (prev === type ? null : type));
  };

  const savedEntityFilters = filters?.arr || [];
  const countSavedFilterSelections = (filter: any) =>
    (filter?.lenses?.length || 0) +
    (filter?.countries?.length || 0) +
    (filter?.agencies?.length || 0) +
    (filter?.lists?.length || 0);
  const saveFilterSummary = [
    { label: 'lenses', count: state?.lenses?.length || 0 },
    { label: 'countries', count: state?.countries?.length || 0 },
    { label: 'agencies', count: state?.agencies?.length || 0 },
    { label: 'lists', count: state?.lists?.length || 0 },
  ].filter((item) => item.count > 0);

  const handleToggleSavedFilters = () => {
    if (isInteractionDisabled) {
      return;
    }
    setActiveEntityParameter(null);
    setSavedFiltersOpen((prev) => !prev);
  };

  const handleClearEntityFilters = () => {
    if (
      isInteractionDisabled ||
      !setEntityFilterState ||
      !setEntityFilterSubmit
    ) {
      return;
    }
    setEntityFilterState({
      ...state,
      lenses: [],
      countries: [],
      agencies: [],
      lists: [],
    });
    setEntityFilterSubmit({
      trigger: !entityFilterSubmit?.trigger,
      reason: 'apply',
      location: 'Entity',
    });
    if (setAppliedFilter) {
      setAppliedFilter({});
    }
    setSavedFiltersOpen(false);
  };

  const entityDateRange = state?.dateRange || [];
  const entityDateLabel =
    entityDateRange.length >= 2
      ? `${entityDateRange[0]} – ${entityDateRange[1]}`
      : entityDateRange.length === 1
      ? entityDateRange[0]
      : '';
  const screeningDateLabel = entityDateLabel;

  const handleClearDates = () => {
    if (
      isInteractionDisabled ||
      !setEntityFilterState ||
      !setEntityFilterSubmit
    ) {
      return;
    }
    setEntityFilterState({
      ...state,
      date: null,
      endDate: null,
      dateRange: [],
    });
    setEntityFilterSubmit({
      trigger: !entityFilterSubmit?.trigger,
      reason: 'apply',
      location: 'Entity',
    });
  };

  const handleApplySavedFilter = (filter: any) => {
    if (
      isInteractionDisabled ||
      !setEntityFilterState ||
      !setEntityFilterSubmit
    ) {
      return;
    }
    setEntityFilterState({
      ...state,
      lenses: filter.lenses || [],
      countries: filter.countries || [],
      agencies: filter.agencies || [],
      lists: filter.lists || [],
      fuzzy: true,
    });
    setEntityFilterSubmit({
      trigger: !entityFilterSubmit?.trigger,
      reason: 'apply',
      location: 'Entity',
    });
    if (setAppliedFilter) {
      setAppliedFilter(filter);
    }
    setSavedFiltersOpen(false);
  };

  const handleDeleteSavedFilter = async (id: string) => {
    if (!setFilters) {
      return;
    }
    const res = await DataSearchAPI.deleteSearchFilter(id);
    if (res?.success) {
      setFilters({
        isLoaded: true,
        arr: savedEntityFilters.filter((filt: any) => filt._id !== id),
      });
      if (appliedFilter?._id === id && setAppliedFilter) {
        setAppliedFilter({});
      }
      enqueueSnackbar('Filter deleted', { variant: 'success' });
    } else {
      enqueueSnackbar('Error deleting filter. Please try again.', {
        variant: 'error',
      });
    }
  };

  const handleSaveFilter = async (name: string) => {
    if (!setFilters) {
      return;
    }
    const query = {
      userId: cur_user?.basics?.id,
      groupId: cur_user?.selected_team?.id,
      filterName: name,
      type: 'entity',
      lenses: state.lenses,
      countries: state.countries,
      agencies: state.agencies,
      lists: state.lists,
      entRes: true,
    };
    const res = await DataSearchAPI.createSearchFilter({
      newSearchFilter: query,
    });
    if (res?.success) {
      setFilters({ isLoaded: true, arr: [...savedEntityFilters, res.result] });
      if (setAppliedFilter) {
        setAppliedFilter(res.result);
      }
      setSaveFilterModalOpen(false);
      enqueueSnackbar('Successfully saved your filter', { variant: 'success' });
    } else {
      enqueueSnackbar('Error saving your filter. Please try again.', {
        variant: 'error',
      });
    }
  };
  const handleUpdateSavedFilter = async () => {
    if (!setFilters || !appliedFilter?._id) {
      return;
    }
    const query = {
      ...appliedFilter,
      lenses: state.lenses,
      countries: state.countries,
      agencies: state.agencies,
      lists: state.lists,
      entRes: true,
    };
    const res = await DataSearchAPI.updateSearchFilter({ update: query });
    if (res?.success) {
      const arr = savedEntityFilters.map((filt: any) =>
        filt._id === res.result._id ? res.result : filt,
      );
      setFilters({ isLoaded: true, arr: arr });
      if (setAppliedFilter) {
        setAppliedFilter(res.result);
      }
      enqueueSnackbar('Successfully updated your filter', {
        variant: 'success',
      });
    } else {
      enqueueSnackbar('Error updating your filter. Please try again.', {
        variant: 'error',
      });
    }
  };
  const handleEntityParameterSelect = (config: any, value: string) => {
    if (
      isInteractionDisabled ||
      !setEntityFilterState ||
      !setEntityFilterSubmit
    ) {
      return;
    }

    const currentSelections = state?.[config.type] || [];
    const nextSelections = currentSelections.includes(value)
      ? currentSelections.filter((item: string) => item !== value)
      : [...currentSelections, value];

    setEntityFilterState({
      ...state,
      [config.type]: nextSelections,
    });
    setEntityFilterSubmit({
      trigger: !entityFilterSubmit?.trigger,
      reason: config.reason,
      location: 'Entity',
    });
  };

  const savedProductListFilters = listFilters?.arr || [];
  const savedProductCodeFilters = codeFilters?.arr || [];
  const countSavedProductListSelections = (filter: any) =>
    (filter?.lenses?.length || 0) +
    (filter?.countries?.length || 0) +
    (filter?.agencies?.length || 0) +
    (filter?.lists?.length || 0);
  const countSavedProductCodeSelections = (filter: any) =>
    (filter?.hsCodeLists?.length || 0) +
    (filter?.sections?.length || 0) +
    (filter?.chapters?.length || 0) +
    (filter?.codes?.length || 0);
  const productListSaveSummary = [
    { label: 'lenses', count: state?.comLenses?.length || 0 },
    { label: 'countries', count: state?.countries?.length || 0 },
    { label: 'agencies', count: state?.agencies?.length || 0 },
    { label: 'lists', count: state?.lists?.length || 0 },
  ].filter((item) => item.count > 0);
  const productCodeSaveSummary = [
    { label: 'HTS code lists', count: state?.hsCodeLists?.length || 0 },
    { label: 'sections', count: state?.sections?.length || 0 },
    { label: 'chapters', count: state?.chapters?.length || 0 },
    { label: 'HTS codes', count: state?.codes?.length || 0 },
  ].filter((item) => item.count > 0);
  const hasProductListSelections = productListSaveSummary.length > 0;
  const hasProductCodeSelections = productCodeSaveSummary.length > 0;

  const getProductParameterLabel = (
    parameterType: string,
    placeholder: string,
  ) => {
    const selections = state?.[parameterType] || [];
    if (!selections.length) {
      return placeholder;
    }
    if (selections.length === 1) {
      return selections[0];
    }
    return `${selections.length} selected`;
  };

  const getProductParameterOptions = (config: any) => {
    const rawOptions = config?.options?.data || [];
    const seen = new Set<string>();
    const deduped: any[] = [];
    rawOptions.forEach((option: any) => {
      const value = String(option?.key ?? option).trim();
      if (!value || seen.has(value)) {
        return;
      }
      seen.add(value);
      deduped.push(option);
    });
    const currentSelections: string[] = state?.[config.type] || [];
    currentSelections.forEach((value: string) => {
      const trimmed = String(value).trim();
      if (!trimmed || seen.has(trimmed)) {
        return;
      }
      seen.add(trimmed);
      deduped.push({ key: trimmed });
    });
    const query = productParameterSearch.toLowerCase();
    const filtered = query
      ? deduped.filter((option: any) =>
          String(option?.key || option)
            .toLowerCase()
            .includes(query),
        )
      : deduped;
    const selectedSet = new Set(currentSelections);
    return [
      ...filtered.filter((option: any) =>
        selectedSet.has(String(option?.key || option)),
      ),
      ...filtered.filter(
        (option: any) => !selectedSet.has(String(option?.key || option)),
      ),
    ];
  };

  const handleProductParameterToggle = (parameterType: any) => {
    if (isInteractionDisabled) {
      return;
    }
    setProductParameterSearch('');
    setSavedProductListFiltersOpen(false);
    setSavedProductCodeFiltersOpen(false);
    setActiveProductParameter((prev) =>
      prev === parameterType ? null : parameterType,
    );
  };

  const handleProductParameterSelect = (config: any, value: string) => {
    if (
      isInteractionDisabled ||
      !setCommodityFilterState ||
      !setCommodityFilterSubmit
    ) {
      return;
    }

    const currentSelections = state?.[config.type] || [];
    const nextSelections = currentSelections.includes(value)
      ? currentSelections.filter((item: string) => item !== value)
      : [...currentSelections, value];

    setCommodityFilterState({
      ...state,
      [config.type]: nextSelections,
    });
    setCommodityFilterSubmit({
      trigger: !commodityFilterSubmit?.trigger,
      reason: config.reason,
      location: 'Commodity',
    });
  };

  const handleToggleProductSavedFilters = (kind: 'list' | 'code') => {
    if (isInteractionDisabled) {
      return;
    }
    setActiveProductParameter(null);
    if (kind === 'list') {
      setSavedProductCodeFiltersOpen(false);
      setSavedProductListFiltersOpen((prev) => !prev);
    } else {
      setSavedProductListFiltersOpen(false);
      setSavedProductCodeFiltersOpen((prev) => !prev);
    }
  };

  const handleClearProductFilters = (kind: 'list' | 'code') => {
    if (
      isInteractionDisabled ||
      !setCommodityFilterState ||
      !setCommodityFilterSubmit
    ) {
      return;
    }

    if (kind === 'list') {
      setCommodityFilterState({
        ...state,
        comLenses: [],
        countries: [],
        agencies: [],
        lists: [],
      });
      if (setAppliedListFilter) {
        setAppliedListFilter({});
      }
      setSavedProductListFiltersOpen(false);
    } else {
      setCommodityFilterState({
        ...state,
        hsCodeLists: [],
        sections: [],
        chapters: [],
        codes: [],
      });
      if (setAppliedCodeFilter) {
        setAppliedCodeFilter({});
      }
      setSavedProductCodeFiltersOpen(false);
    }
    setCommodityFilterSubmit({
      trigger: !commodityFilterSubmit?.trigger,
      reason: 'apply',
      location: 'Commodity',
    });
  };

  const handleApplyProductSavedFilter = (
    kind: 'list' | 'code',
    filter: any,
  ) => {
    if (
      isInteractionDisabled ||
      !setCommodityFilterState ||
      !setCommodityFilterSubmit
    ) {
      return;
    }

    if (kind === 'list') {
      setCommodityFilterState({
        ...state,
        comLenses: filter.lenses || [],
        countries: filter.countries || [],
        agencies: filter.agencies || [],
        lists: filter.lists || [],
        fuzzy: filter.entRes,
      });
      if (setAppliedListFilter) {
        setAppliedListFilter(filter);
      }
      setSavedProductListFiltersOpen(false);
    } else {
      setCommodityFilterState({
        ...state,
        hsCodeLists: filter.hsCodeLists || [],
        sections: filter.sections || [],
        chapters: filter.chapters || [],
        codes: filter.codes || [],
        fuzzy: filter.entRes,
      });
      if (setAppliedCodeFilter) {
        setAppliedCodeFilter(filter);
      }
      setSavedProductCodeFiltersOpen(false);
    }
    setCommodityFilterSubmit({
      trigger: !commodityFilterSubmit?.trigger,
      reason: 'apply',
      location: 'Commodity',
    });
  };

  const handleDeleteProductSavedFilter = async (
    kind: 'list' | 'code',
    id: string,
  ) => {
    const setter = kind === 'list' ? setListFilters : setCodeFilters;
    if (!setter) {
      return;
    }
    const savedFilters =
      kind === 'list' ? savedProductListFilters : savedProductCodeFilters;
    const applied = kind === 'list' ? appliedListFilter : appliedCodeFilter;
    const setApplied =
      kind === 'list' ? setAppliedListFilter : setAppliedCodeFilter;

    const res = await DataSearchAPI.deleteSearchFilter(id);
    if (res?.success) {
      setter({
        isLoaded: true,
        arr: savedFilters.filter((filt: any) => filt._id !== id),
      });
      if (applied?._id === id && setApplied) {
        setApplied({});
      }
      enqueueSnackbar('Filter deleted', { variant: 'success' });
    } else {
      enqueueSnackbar('Error deleting filter. Please try again.', {
        variant: 'error',
      });
    }
  };

  const openProductSaveFilterModal = (kind: 'list' | 'code') => {
    setProductSaveFilterTarget(kind);
    setProductSaveFilterModalOpen(true);
  };

  const handleSaveProductFilter = async (name: string) => {
    const isListFilter = productSaveFilterTarget === 'list';
    const setter = isListFilter ? setListFilters : setCodeFilters;
    const savedFilters = isListFilter
      ? savedProductListFilters
      : savedProductCodeFilters;
    if (!setter) {
      return;
    }

    const query = isListFilter
      ? {
          userId: cur_user?.basics?.id,
          groupId: cur_user?.selected_team?.id,
          filterName: name,
          type: 'commodityList',
          lenses: state.comLenses,
          countries: state.countries,
          agencies: state.agencies,
          lists: state.lists,
          entRes: state.fuzzy,
        }
      : {
          userId: cur_user?.basics?.id,
          groupId: cur_user?.selected_team?.id,
          filterName: name,
          type: 'commodityCode',
          hsCodeLists: state.hsCodeLists,
          sections: state.sections,
          chapters: state.chapters,
          codes: state.codes,
          entRes: state.fuzzy,
        };

    const res = await DataSearchAPI.createSearchFilter({
      newSearchFilter: query,
    });
    if (res?.success) {
      setter({ isLoaded: true, arr: [...savedFilters, res.result] });
      if (isListFilter && setAppliedListFilter) {
        setAppliedListFilter(res.result);
      }
      if (!isListFilter && setAppliedCodeFilter) {
        setAppliedCodeFilter(res.result);
      }
      setProductSaveFilterModalOpen(false);
      enqueueSnackbar('Successfully saved your filter', { variant: 'success' });
    } else {
      enqueueSnackbar('Error saving your filter. Please try again.', {
        variant: 'error',
      });
    }
  };

  const handleUpdateProductSavedFilter = async (kind: 'list' | 'code') => {
    const isListFilter = kind === 'list';
    const setter = isListFilter ? setListFilters : setCodeFilters;
    const savedFilters = isListFilter
      ? savedProductListFilters
      : savedProductCodeFilters;
    const applied = isListFilter ? appliedListFilter : appliedCodeFilter;
    const setApplied = isListFilter
      ? setAppliedListFilter
      : setAppliedCodeFilter;
    if (!setter || !applied?._id) {
      return;
    }

    const query = isListFilter
      ? {
          ...applied,
          lenses: state.comLenses,
          countries: state.countries,
          agencies: state.agencies,
          lists: state.lists,
          entRes: state.fuzzy,
        }
      : {
          ...applied,
          hsCodeLists: state.hsCodeLists,
          sections: state.sections,
          chapters: state.chapters,
          codes: state.codes,
          entRes: state.fuzzy,
        };
    const res = await DataSearchAPI.updateSearchFilter({ update: query });
    if (res?.success) {
      setter({
        isLoaded: true,
        arr: savedFilters.map((filt: any) =>
          filt._id === res.result._id ? res.result : filt,
        ),
      });
      if (setApplied) {
        setApplied(res.result);
      }
      enqueueSnackbar('Successfully updated your filter', {
        variant: 'success',
      });
    } else {
      enqueueSnackbar('Error updating your filter. Please try again.', {
        variant: 'error',
      });
    }
  };

  const renderProductSavedFilterBar = (kind: 'list' | 'code') => {
    const isListFilter = kind === 'list';
    const savedFilters = isListFilter
      ? savedProductListFilters
      : savedProductCodeFilters;
    const applied = isListFilter ? appliedListFilter : appliedCodeFilter;
    const isOpen = isListFilter
      ? savedProductListFiltersOpen
      : savedProductCodeFiltersOpen;
    const wrapRef = isListFilter
      ? savedProductListFiltersWrapRef
      : savedProductCodeFiltersWrapRef;
    const countSelections = isListFilter
      ? countSavedProductListSelections
      : countSavedProductCodeSelections;
    const hasSelections = isListFilter
      ? hasProductListSelections
      : hasProductCodeSelections;
    const label = isListFilter ? 'Saved list filters' : 'Saved HTS filters';

    return (
      <div className='entity-screening-saved-bar product-screening-saved-bar'>
        <div className='entity-screening-saved-select-wrap' ref={wrapRef}>
          <button
            type='button'
            className={`entity-screening-saved-select${
              isOpen ? ' active' : ''
            }`}
            onClick={() => handleToggleProductSavedFilters(kind)}
            disabled={isInteractionDisabled}
          >
            <span className='entity-screening-saved-select-label'>
              <BookmarkBorderOutlinedIcon sx={{ fontSize: 18 }} />
              {applied?.filterName || label}
            </span>
            <ExpandMoreOutlinedIcon sx={{ fontSize: 22 }} />
          </button>
          {isOpen && (
            <div className='entity-screening-saved-menu'>
              {savedFilters.length ? (
                savedFilters.map((filter: any) => {
                  const selected = applied?._id === filter._id;
                  return (
                    <div
                      className={`entity-screening-saved-menu-item${
                        selected ? ' active' : ''
                      }`}
                      key={filter._id}
                    >
                      <button
                        type='button'
                        className='entity-screening-saved-menu-apply'
                        onClick={() =>
                          handleApplyProductSavedFilter(kind, filter)
                        }
                      >
                        <span
                          className={`entity-screening-saved-menu-check${
                            selected ? ' selected' : ''
                          }`}
                        >
                          {selected && <CheckIcon sx={{ fontSize: 14 }} />}
                        </span>
                        <span className='entity-screening-saved-menu-name'>
                          {filter.filterName}
                        </span>
                        <span className='entity-screening-saved-menu-count'>
                          {countSelections(filter)} filters
                        </span>
                      </button>
                      <button
                        type='button'
                        className='entity-screening-saved-menu-delete'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProductSavedFilter(kind, filter._id);
                        }}
                        aria-label={`Delete ${filter.filterName}`}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </button>
                    </div>
                  );
                })
              ) : (
                <span className='entity-screening-saved-menu-empty'>
                  No saved filters yet
                </span>
              )}
            </div>
          )}
        </div>
        <div className='entity-screening-saved-actions'>
          <button
            type='button'
            className='entity-screening-saved-clear-btn'
            onClick={() => handleClearProductFilters(kind)}
            disabled={isInteractionDisabled || !hasSelections}
          >
            Clear
          </button>
          {applied?._id && (
            <button
              type='button'
              className='entity-screening-saved-update-btn'
              onClick={() => handleUpdateProductSavedFilter(kind)}
              disabled={isInteractionDisabled}
            >
              Update
            </button>
          )}
          <button
            type='button'
            className='entity-screening-saved-save-btn'
            onClick={() => openProductSaveFilterModal(kind)}
            disabled={isInteractionDisabled || !hasSelections}
          >
            <SaveOutlinedIcon sx={{ fontSize: 18 }} />
            Save selection
          </button>
        </div>
      </div>
    );
  };

  const renderProductParameterGrid = (configs: readonly any[], ref: any) => (
    <div className='entity-screening-parameter-grid' ref={ref}>
      {configs.map((config: any) => {
        const selections = state?.[config.type] || [];
        const isActive = activeProductParameter === config.type;
        const isLoading = config.options?.isLoading;
        const options = getProductParameterOptions(config);

        return (
          <div
            key={config.type}
            className={`entity-screening-parameter-field${
              isActive ? ' active' : ''
            }`}
          >
            <label>
              {config.icon}
              {config.title}
            </label>
            <button
              type='button'
              className={`entity-screening-parameter-select${
                isActive ? ' active' : ''
              }`}
              onClick={() => handleProductParameterToggle(config.type)}
              disabled={isInteractionDisabled}
            >
              <span>
                {getProductParameterLabel(config.type, config.placeholder)}
              </span>
              <ExpandMoreOutlinedIcon sx={{ fontSize: 22 }} />
            </button>
            {isActive && (
              <div className='entity-screening-parameter-menu'>
                <div className='entity-screening-parameter-search'>
                  <SearchIcon sx={{ fontSize: 18 }} />
                  <input
                    type='text'
                    value={productParameterSearch}
                    onChange={(e) => setProductParameterSearch(e.target.value)}
                    placeholder={`Search ${config.pluralLabel}`}
                  />
                </div>
                <div className='entity-screening-parameter-options'>
                  {isLoading ? (
                    <span className='entity-screening-parameter-empty'>
                      Loading...
                    </span>
                  ) : options.length ? (
                    options.map((option: any) => {
                      const optionValue = String(option?.key || option);
                      const isSelected = selections.includes(optionValue);

                      return (
                        <button
                          type='button'
                          key={optionValue}
                          className='entity-screening-parameter-option'
                          onClick={() =>
                            handleProductParameterSelect(config, optionValue)
                          }
                        >
                          <span
                            className={`entity-screening-parameter-check${
                              isSelected ? ' selected' : ''
                            }`}
                          />
                          <span>{optionValue}</span>
                        </button>
                      );
                    })
                  ) : (
                    <span className='entity-screening-parameter-empty'>
                      No options available
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
  const handleEntityExport = async () => {
    if (isInteractionDisabled || !getCSVResults || !hasEntityQuery) {
      return;
    }
    const res = await getCSVResults(0, entityExportContext || undefined);
    const hits = res?.[0]?.result?.body?.hits?.hits || [];
    if (hits.length >= 1) {
      exportToCSV(
        entityExportFileName.trim() || 'entity_screening_results',
        [...hits],
        [
          'Name of Entity',
          'Entity Country',
          'List Name',
          'Agency',
          'Additional Remarks',
          'Date Updated',
        ],
        'entity',
      );
    }
  };
  const handleEntityExportFileNameClose = () => {
    setEntityExportFileNameOpen(false);
    setEntityExportFileName('');
    setEntityExportContext(null);
  };
  const handleEntityPageReset = () => {
    if (isInteractionDisabled) {
      return;
    }

    setEntityInputMode('details');
    setEntityInsightPanels({ ai: false, media: false, ownership: false });
    setEntityParametersOpen(false);
    setActiveEntityParameter(null);
    setEntityParameterSearch('');
    setEntityAdverseMedia(null);
    setEntityAdverseMediaError(null);
    setAdverseMedia?.(null);
    setEntityBeneficialOwnership(null);
    setEntityBeneficialOwnershipError(null);
    setEntityExportFileName('');
    setEntityExportFileNameOpen(false);
    setEntityExportContext(null);

    if (handleFullReset) {
      handleFullReset();
    }
  };

  // Switching to the BITE Record ID tab hides the parameters + control bar, so
  // it must also RESET the now-hidden entity-search state — the entity-detail
  // inputs plus the lens / country / agency / list filters, date range, and
  // Include Archived toggle. Otherwise the parent's
  // handleSubmit keeps sending those invisible filters alongside the record ID
  // and an exact lookup can silently return nothing the user can't explain.
  // Everything is reset in single state writes so the calls don't clobber one
  // another off a stale snapshot. Switching back to Entity Details starts clean.
  const handleEnterRecordMode = () => {
    setEntityInputMode('record');
    setEntityParametersOpen(false);
    setActiveEntityParameter(null);

    if (handleFullReset) {
      handleFullReset();
      return;
    }

    // Clear the entity-detail text inputs (keep `record`) in one write.
    if (inputs?.primary || inputs?.country || inputs?.address) {
      setInputs({ ...inputs, primary: '', country: '', address: '' });
    }

    if (setEntityFilterState) {
      setEntityFilterState({
        ...state,
        lenses: [],
        countries: [],
        agencies: [],
        lists: [],
        date: null,
        endDate: null,
        dateRange: [],
        fuzzy: true,
        archived: false,
      });
    }
    if (setAppliedFilter) {
      setAppliedFilter({});
    }
    if (setEntityFilterSubmit) {
      setEntityFilterSubmit({
        trigger: !entityFilterSubmit?.trigger,
        reason: 'apply',
        location: 'Entity',
      });
    }
  };
  const handleEnterDetailsMode = () => {
    if (isInteractionDisabled) {
      return;
    }
    setEntityInputMode('details');
    setEntityParametersOpen(false);
    setActiveEntityParameter(null);
    if (handleFullReset) {
      handleFullReset();
      return;
    }
    setInputs({ ...inputs, record: '' });
    // DECISION: mode switches clear hidden screening parameters so an exact
    // Record ID lookup can never leak constraints into the following name run.
    if (setEntityFilterState) {
      setEntityFilterState({
        ...state,
        lenses: [],
        countries: [],
        agencies: [],
        lists: [],
        date: null,
        endDate: null,
        dateRange: [],
        fuzzy: true,
        archived: false,
      });
    }
    if (setAppliedFilter) {
      setAppliedFilter({});
    }
  };
  const toggleEntityInsightPanel = (key: 'ai' | 'media' | 'ownership') => {
    setEntityInsightPanels((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const handleEntityAdverseMediaCheck = async (
    subject: EntityScreenSubject,
  ) => {
    const entityName = subject.name;
    if (isInteractionDisabled || !entityName || isCheckingEntityAdverseMedia) {
      return;
    }
    logAppAction('entity_screening:view_adverse_media');

    const requestId = ++entityInsightRequestRef.current.media;
    const isCurrent = () => requestId === entityInsightRequestRef.current.media;

    setEntityAdverseMediaError(null);
    setEntityInsightPanels((prev) => ({ ...prev, media: true }));
    try {
      const result = await runEntityAdverseMedia({
        entityName: entityName,
        country: inputs.country,
      });
      if (!isCurrent()) return;
      setEntityAdverseMedia(result);
      setAdverseMedia?.(result);
    } catch (e: any) {
      // Deliberately no fallback. The entity service runs the OSINT search
      // itself, so retrying the same search against pyapi from here would bill
      // a second full AI web search for the same entity -- worst of all in the
      // case that matters most, where this call failed only AFTER the search
      // had already completed server-side (persistence error, or a client
      // timeout while the agent was still running). Surface the failure instead.
      if (!isCurrent()) return;
      setEntityAdverseMedia(null);
      setAdverseMedia?.(null);
      setEntityAdverseMediaError(
        e?.message || 'Failed to check adverse media.',
      );
    }
  };
  const handleEntityBeneficialOwnershipCheck = async (
    subject: EntityScreenSubject,
  ) => {
    const entityName = subject.name;
    if (
      isInteractionDisabled ||
      !entityName ||
      isCheckingEntityBeneficialOwnership ||
      ownershipResults?.isLoading
    ) {
      return;
    }
    logAppAction('entity_screening:view_beneficial_ownership');

    const requestId = ++entityInsightRequestRef.current.ownership;
    const isCurrent = () =>
      requestId === entityInsightRequestRef.current.ownership;

    setEntityBeneficialOwnershipError(null);
    setEntityInsightPanels((prev) => ({ ...prev, ownership: true }));
    // Hand the agent every BOI/PEP record retrieved for the Ownership tab so
    // it reviews the same records the table shows. The agent applies its own
    // exact/close-name matching (lookup_boi) to decide what counts as a hit.
    const ownershipRecords = (
      ownershipResults?.data?.[0]?.result?.body?.hits?.hits || []
    ).map((hit: any) => hit?._source);
    const ownershipPotentialMatchCount =
      ownershipResults?.data?.[0]?.result?.body?.hits?.total?.value ??
      ownershipRecords.length;
    try {
      const result = await runEntityBeneficialOwnership({
        entityName: entityName,
        country: subject.country || inputs.country,
        address: inputs.address,
        records: ownershipRecords,
        potentialMatchCount: ownershipPotentialMatchCount,
      });
      if (!isCurrent()) return;

      /**if (result) {
        setEntityBeneficialOwnership(result);
        // Surface the eval to the parent so it can be persisted when the user
        // saves the screen as a Saved Work ticket.
        setBeneficialOwnership?.(result);
      } else {
        setEntityBeneficialOwnership(null);
        // Clear any previously-surfaced eval so a stale success can't be
        // persisted into a saved ticket after a failed re-check.
        setBeneficialOwnership?.(null);
        setEntityBeneficialOwnershipError(
          'No beneficial ownership results were returned.',
        );
      }
        */

      if (
        result?.checked ||
        result?.summary ||
        result?.beneficial_ownership_summary
      ) {
        setEntityBeneficialOwnership(result);
        setBeneficialOwnership?.(result);
      } else {
        setEntityBeneficialOwnership(null);
        setBeneficialOwnership?.(null);
        setEntityBeneficialOwnershipError(
          'No beneficial ownership results were returned.',
        );
      }
    } catch (e: any) {
      if (!isCurrent()) return;
      setEntityBeneficialOwnership(null);
      setBeneficialOwnership?.(null);
      setEntityBeneficialOwnershipError(
        e?.message || 'Failed to check beneficial ownership.',
      );
    }
  };

  // When the screened entity changes, clear the on-demand insight results so the
  // Adverse Media / Beneficial Ownership checks can be re-run for the new entity
  // (otherwise the stale results stick around and the check buttons stay hidden).
  useEffect(() => {
    // Invalidate any in-flight insight requests so a response that arrives
    // after the entity changed is discarded instead of repopulating the UI.
    entityInsightRequestRef.current.media += 1;
    entityInsightRequestRef.current.ownership += 1;
    setEntityAdverseMedia(null);
    setEntityAdverseMediaError(null);
    setAdverseMedia?.(null);
    setEntityBeneficialOwnership(null);
    setEntityBeneficialOwnershipError(null);
    setBeneficialOwnership?.(null);
  }, [
    inputs.primary,
    inputs.record,
    inputs.country,
    inputs.address,
    setBeneficialOwnership,
    setAdverseMedia,
  ]);

  return (
    <div
      className={`screening-search-container-full${
        usesScreeningRedesign ? ' entity-screening-redesign' : ''
      }`}
    >
      {cyberTab && (
        <div className='filters-tabs-container'>
          <div className='screening-tabs-inner'>
            <div
              className={`screening-tab-btn${
                cyberTab === 'Trade' ? '-selected' : ''
              }`}
              onClick={() => handleTabSwitch('Trade')}
            >
              <div
                className={`screening-tab-btn-text${
                  cyberTab === 'Trade' ? '-selected' : ''
                }`}
              >
                Global Trade Alerts
              </div>
            </div>
            <div
              className={`screening-tab-btn${
                cyberTab === 'CVE' ? '-selected' : ''
              }`}
              onClick={() => handleTabSwitch('CVE')}
            >
              <div
                className={`screening-tab-btn-text${
                  cyberTab === 'CVE' ? '-selected' : ''
                }`}
              >
                Common Cyber Vulnerabilities & Exposures
              </div>
            </div>
            <div
              className={`screening-tab-btn${
                cyberTab === 'Markets' ? '-selected' : ''
              }`}
              onClick={() => handleTabSwitch('Markets')}
            >
              <div
                className={`screening-tab-btn-text${
                  cyberTab === 'Markets' ? '-selected' : ''
                }`}
              >
                Notorious Markets
              </div>
            </div>
          </div>
        </div>
      )}
      {isEntitySearch && (
        <EntityScreeningView
          inputs={inputs}
          setInputs={setInputs}
          state={state}
          setState={setEntityFilterState}
          results={results}
          ownershipResults={ownershipResults}
          aiSums={aiSums}
          aiLoading={aiLoading}
          hasRunOnce={hasRunOnce}
          page={page}
          setPage={setPage}
          handleSubmit={handleSubmit}
          entityFilterOptions={entityFilterOptions}
          screenFilters={entityScreenFilters}
          unfilteredMatchTotal={unfilteredMatchTotal}
          tableResetSignal={tableResetSignal}
          isInteractionDisabled={isInteractionDisabled}
          isWhiteLabel={isWhiteLabel}
          isRestricted={isRestricted}
          entityInputMode={entityInputMode}
          onEnterDetailsMode={handleEnterDetailsMode}
          onEnterRecordMode={handleEnterRecordMode}
          onSaveTicket={createTicket}
          savingTicket={Boolean(saving)}
          setExportFileNameOpen={setEntityExportFileNameOpen}
          onExportContext={setEntityExportContext}
          adverseMedia={entityAdverseMedia}
          adverseMediaError={entityAdverseMediaError}
          adverseMediaLoading={isCheckingEntityAdverseMedia}
          beneficialOwnership={entityBeneficialOwnership}
          beneficialOwnershipError={entityBeneficialOwnershipError}
          beneficialOwnershipLoading={isCheckingEntityBeneficialOwnership}
          onRunAdverseMedia={handleEntityAdverseMediaCheck}
          onRunBeneficialOwnership={handleEntityBeneficialOwnershipCheck}
          onOpenDate={handleOpenDate}
          onClearDate={handleClearDates}
          screenReason={entityScreenReason}
          recentScreensStorageKey={`bite:recent-entity-screens:${
            cur_user?.selected_team?.id || 'no-team'
          }:${cur_user?.basics?.id || cur_user?.basics?.email || 'no-user'}`}
        />
      )}
      {isEntitySearch && false && (
        <>
          <div className='entity-screening-hero'>
            <div>
              <h1 className='entity-screening-title'>Entity Screening</h1>
              <p className='entity-screening-subtitle'>
                Screen entities against sanctions, watchlists, and risk sources
              </p>
            </div>
            <div className='entity-screening-hero-actions'>
              <ModuleContentTooltip
                title={
                  <p className='module-header-tooltip-text'>
                    Search entities by name or BITE Record ID, adjust screening
                    parameters, and review matching risk records.
                  </p>
                }
                placement='bottom'
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      display: 'flex',
                      padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      alignSelf: 'stretch',
                      borderRadius: 'var(--radius-md, 8px)',
                      background: 'var(--Background-200, var(--bg-colors))',
                      color: 'var(--text-prim25)',
                      maxWidth: '400px',
                      '& .MuiTooltip-arrow': {
                        color: 'var(--Background-200, var(--bg-colors))',
                      },
                    },
                  },
                }}
              >
                <button type='button' className='entity-screening-help-btn'>
                  <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                  Help
                </button>
              </ModuleContentTooltip>
              {/* <button
                type='button'
                className='entity-screening-action-btn'
              >
                Recent Screens
                <ExpandMoreOutlinedIcon sx={{ fontSize: 18 }} />
              </button> */}
              <button
                type='button'
                className='entity-screening-action-btn'
                onClick={() =>
                  runIfEnabled(() => setEntityExportFileNameOpen(true))
                }
                disabled={
                  isInteractionDisabled ||
                  !hasEntityQuery ||
                  !entityResultsTotal
                }
              >
                <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                Export
              </button>
              {!isWhiteLabel && (
                <button
                  type='button'
                  className='entity-screening-action-btn'
                  onClick={() => runIfEnabled(() => setTicketNameOpen(true))}
                  disabled={
                    isInteractionDisabled ||
                    !hasEntityQuery ||
                    !hasCompletedScreen
                  }
                >
                  <SaveOutlinedIcon sx={{ fontSize: 18 }} />
                  Save
                </button>
              )}
            </div>
          </div>

          <div className='entity-screening-form-panel'>
            <div className='entity-screening-segmented'>
              <button
                type='button'
                className={entityInputMode === 'details' ? 'active' : ''}
                onClick={() => {
                  setEntityInputMode('details');
                  // Drop the now-hidden BITE Record ID so it can't drive actions.
                  if (inputs.record) {
                    handleClearInput('record');
                  }
                }}
              >
                Entity Details
              </button>
              <button
                type='button'
                className={entityInputMode === 'record' ? 'active' : ''}
                onClick={handleEnterRecordMode}
              >
                BITE Record ID
              </button>
            </div>

            {entityInputMode === 'details' ? (
              <div className='entity-screening-input-grid'>
                <label className='entity-screening-field'>
                  <span>Entity Name</span>
                  <div className='screening-input-field-container entity-screening-input'>
                    <PersonOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                    <input
                      id='primary'
                      type='text'
                      placeholder='Enter person or company name'
                      className='screening-input-field-content'
                      name='primary'
                      value={inputs.primary}
                      onChange={(e) => handleChange(e)}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter' && !isInteractionDisabled) {
                          handleSubmit(page);
                        }
                      }}
                      disabled={isInteractionDisabled}
                    />
                    {inputs.primary && (
                      <span
                        className='entity-screening-input-clear'
                        role='button'
                        aria-label='Clear entity name'
                        onClick={() =>
                          runIfEnabled(() => handleClearInput('primary'))
                        }
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </span>
                    )}
                  </div>
                </label>

                <label className='entity-screening-field'>
                  <span>Country of Interest (Optional)</span>
                  <Autocomplete
                    options={screeningCountries as COpt[]}
                    getOptionLabel={(option: any) => displayLabel(option)}
                    isOptionEqualToValue={(opt: any, val: any) =>
                      (Boolean(getIso(opt)) && getIso(opt) === getIso(val)) ||
                      getName(opt) === getName(val)
                    }
                    clearOnEscape
                    componentsProps={{
                      popper: {
                        sx: {
                          '& .MuiAutocomplete-option': {
                            color: 'var(--text-primary-white) !important',
                          },
                        },
                      },
                      paper: {
                        sx: {
                          color: 'var(--text-primary-white) !important',
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '44px',
                        borderRadius: '8px',
                        background: 'var(--Background-200, var(--bg-colors))',
                        border: '1px solid var(--es-soft-border)',
                        color: 'var(--text-primary-color)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      },
                      '& .MuiAutocomplete-inputRoot': {
                        paddingTop: '0 !important',
                        paddingBottom: '0 !important',
                      },
                      '& .MuiInputBase-input': {
                        color: 'var(--text-primary-color)',
                        fontFamily: 'Inter',
                        fontSize: '14px',
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'var(--text-secondary)',
                      },
                      '& .MuiAutocomplete-clearIndicator': {
                        display: 'none',
                      },
                    }}
                    onChange={(_: any, newValue: string) =>
                      handleCountryChange(newValue)
                    }
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        placeholder='Select country'
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <PublicOutlinedIcon
                                sx={{
                                  color: 'var(--text-secondary)',
                                  fontSize: 20,
                                  marginRight: '8px',
                                }}
                              />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {inputs.country && (
                                <span
                                  className='entity-screening-input-clear'
                                  role='button'
                                  aria-label='Clear country'
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() =>
                                    runIfEnabled(() =>
                                      handleClearInput('country'),
                                    )
                                  }
                                >
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </span>
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    value={findCountryOption(inputs.country)}
                    disabled={isInteractionDisabled}
                  />
                </label>

                <label className='entity-screening-field'>
                  <span>Address (Optional)</span>
                  <div className='screening-input-field-container entity-screening-input'>
                    <LocationOnOutlinedIcon sx={{ fontSize: 20 }} />
                    <input
                      id='address'
                      type='text'
                      placeholder='Enter address or location'
                      className='screening-input-field-content'
                      name='address'
                      value={inputs.address}
                      onChange={(e) => handleChange(e)}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter' && !isInteractionDisabled) {
                          handleSubmit(page);
                        }
                      }}
                      disabled={isInteractionDisabled}
                    />
                    {inputs.address && (
                      <span
                        className='entity-screening-input-clear'
                        role='button'
                        aria-label='Clear address'
                        onClick={() =>
                          runIfEnabled(() => handleClearInput('address'))
                        }
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </span>
                    )}
                  </div>
                </label>
              </div>
            ) : (
              <div className='entity-screening-record-row'>
                <label
                  className='entity-screening-record-label'
                  htmlFor='record'
                >
                  BITE Record ID
                </label>
                {/* Input + Run sit on one centered row. Both are 44px tall, so
                    align-items:center lines them up exactly; the label above
                    spans the whole row. */}
                <div className='entity-screening-record-line'>
                  <div className='screening-input-field-container entity-screening-input entity-screening-record-input'>
                    <ArticleOutlinedIcon sx={{ fontSize: 20 }} />
                    <input
                      id='record'
                      type='text'
                      placeholder='Enter BITE Record ID'
                      className='screening-input-field-content'
                      name='record'
                      value={inputs.record}
                      onChange={(e) => handleChange(e)}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter' && !isInteractionDisabled) {
                          handleSubmit(page);
                        }
                      }}
                      disabled={isInteractionDisabled}
                    />
                    {inputs.record && (
                      <span
                        className='entity-screening-input-clear'
                        role='button'
                        aria-label='Clear BITE Record ID'
                        onClick={() =>
                          runIfEnabled(() => handleClearInput('record'))
                        }
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </span>
                    )}
                  </div>
                  <button
                    type='button'
                    className='entity-screening-run-btn'
                    onClick={() => runIfEnabled(() => handleSubmit(page))}
                    disabled={isInteractionDisabled}
                  >
                    {isInteractionDisabled ? (
                      <LockIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <SearchIcon sx={{ fontSize: 20 }} />
                    )}
                    Run Screening
                  </button>
                </div>
              </div>
            )}

            {entityInputMode !== 'record' && (
              <section className='entity-screening-parameters-panel'>
                <button
                  type='button'
                  className={`entity-screening-parameters-header${
                    entityParametersOpen ? ' open' : ''
                  }`}
                  onClick={() => setEntityParametersOpen((prev) => !prev)}
                >
                  <span>
                    <FilterListIcon sx={{ fontSize: 20 }} />
                    <strong>Screening Parameters</strong>
                    {(totalAppliedParameters > 0 || !entityParametersOpen) && (
                      <em>
                        {totalAppliedParameters
                          ? `${totalAppliedParameters} filters applied`
                          : 'Defaults to all lenses, countries, agencies, and lists'}
                      </em>
                    )}
                  </span>
                  <ExpandMoreOutlinedIcon
                    className={entityParametersOpen ? 'open' : ''}
                    sx={{ fontSize: 22 }}
                  />
                </button>

                {entityParametersOpen && (
                  <div className='entity-screening-parameters-body'>
                    <div className='entity-screening-saved-bar'>
                      <div
                        className='entity-screening-saved-select-wrap'
                        ref={savedFiltersWrapRef}
                      >
                        <button
                          type='button'
                          className={`entity-screening-saved-select${
                            savedFiltersOpen ? ' active' : ''
                          }`}
                          onClick={handleToggleSavedFilters}
                          disabled={isInteractionDisabled}
                        >
                          <span className='entity-screening-saved-select-label'>
                            <BookmarkBorderOutlinedIcon sx={{ fontSize: 18 }} />
                            {appliedFilter?.filterName || 'Saved filters'}
                          </span>
                          <ExpandMoreOutlinedIcon sx={{ fontSize: 20 }} />
                        </button>
                        {savedFiltersOpen && (
                          <div className='entity-screening-saved-menu'>
                            {savedEntityFilters.length ? (
                              savedEntityFilters.map((filt: any) => {
                                const isApplied =
                                  appliedFilter?._id === filt._id;
                                return (
                                  <div
                                    key={filt._id}
                                    className={`entity-screening-saved-menu-item${
                                      isApplied ? ' active' : ''
                                    }`}
                                  >
                                    <button
                                      type='button'
                                      className='entity-screening-saved-menu-apply'
                                      onClick={() =>
                                        handleApplySavedFilter(filt)
                                      }
                                    >
                                      <span
                                        className={`entity-screening-saved-menu-check${
                                          isApplied ? ' selected' : ''
                                        }`}
                                      >
                                        {isApplied && (
                                          <CheckIcon sx={{ fontSize: 13 }} />
                                        )}
                                      </span>
                                      <span className='entity-screening-saved-menu-name'>
                                        {filt.filterName}
                                      </span>
                                      <span className='entity-screening-saved-menu-count'>
                                        {countSavedFilterSelections(filt)}{' '}
                                        filters
                                      </span>
                                    </button>
                                    <button
                                      type='button'
                                      className='entity-screening-saved-menu-delete'
                                      onClick={() =>
                                        handleDeleteSavedFilter(filt._id)
                                      }
                                      aria-label='Delete saved filter'
                                    >
                                      <DeleteOutlineIcon
                                        sx={{ fontSize: 17 }}
                                      />
                                    </button>
                                  </div>
                                );
                              })
                            ) : (
                              <span className='entity-screening-saved-menu-empty'>
                                No saved filters yet. Make selections and save
                                them to reuse later.
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className='entity-screening-saved-actions'>
                        {totalAppliedParameters > 0 && (
                          <button
                            type='button'
                            className='entity-screening-saved-clear-btn'
                            onClick={handleClearEntityFilters}
                            disabled={isInteractionDisabled}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                            Clear filters
                          </button>
                        )}
                        {appliedFilter?._id && (
                          <button
                            type='button'
                            className='entity-screening-saved-update-btn'
                            onClick={handleUpdateSavedFilter}
                            disabled={isInteractionDisabled}
                          >
                            Update
                          </button>
                        )}
                        <button
                          type='button'
                          className='entity-screening-saved-save-btn'
                          onClick={() => setSaveFilterModalOpen(true)}
                          disabled={
                            isInteractionDisabled ||
                            totalAppliedParameters === 0
                          }
                        >
                          <SaveOutlinedIcon sx={{ fontSize: 18 }} />
                          Save selection
                        </button>
                      </div>
                    </div>
                    <p className='entity-screening-parameters-note'>
                      <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                      Selections cascade left-to-right. Choosing a lens narrows
                      the available countries, which narrows the agencies, and
                      so on.
                    </p>
                    <div
                      className='entity-screening-parameter-grid'
                      ref={entityParameterGridRef}
                    >
                      {entityParameterConfigs.map((config) => {
                        const selections = state?.[config.type] || [];
                        const isActive = activeEntityParameter === config.type;
                        const isLoading = config.options?.isLoading;
                        const options = getEntityParameterOptions(config);

                        return (
                          <div
                            key={config.type}
                            className={`entity-screening-parameter-field${
                              isActive ? ' active' : ''
                            }`}
                          >
                            <label>
                              {config.icon}
                              {config.title}
                            </label>
                            <button
                              type='button'
                              className={`entity-screening-parameter-select${
                                isActive ? ' active' : ''
                              }`}
                              onClick={() =>
                                handleEntityParameterToggle(config.type)
                              }
                              disabled={isInteractionDisabled}
                            >
                              <span>
                                {getEntityParameterLabel(
                                  config.type,
                                  config.placeholder,
                                )}
                              </span>
                              <ExpandMoreOutlinedIcon sx={{ fontSize: 22 }} />
                            </button>
                            {isActive && (
                              <div className='entity-screening-parameter-menu'>
                                <div className='entity-screening-parameter-search'>
                                  <SearchIcon sx={{ fontSize: 18 }} />
                                  <input
                                    type='text'
                                    value={entityParameterSearch}
                                    onChange={(e) =>
                                      setEntityParameterSearch(e.target.value)
                                    }
                                    placeholder={`Search ${config.pluralLabel}`}
                                  />
                                </div>
                                <div className='entity-screening-parameter-options'>
                                  {isLoading ? (
                                    <span className='entity-screening-parameter-empty'>
                                      Loading...
                                    </span>
                                  ) : options.length ? (
                                    options.map((option: any) => {
                                      const optionValue = String(
                                        option?.key || option,
                                      );
                                      const isSelected =
                                        selections.includes(optionValue);

                                      return (
                                        <button
                                          type='button'
                                          key={optionValue}
                                          className='entity-screening-parameter-option'
                                          onClick={() =>
                                            handleEntityParameterSelect(
                                              config,
                                              optionValue,
                                            )
                                          }
                                        >
                                          <span
                                            className={`entity-screening-parameter-check${
                                              isSelected ? ' selected' : ''
                                            }`}
                                          />
                                          <span>{optionValue}</span>
                                        </button>
                                      );
                                    })
                                  ) : (
                                    <span className='entity-screening-parameter-empty'>
                                      No options available
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Details mode shows the full control bar; the Record ID tab
                renders its input + Run inline above and skips this entirely. */}
            {entityInputMode !== 'record' && (
              <div className='entity-screening-controls'>
                <div className='entity-screening-switch-group'>
                  <CustomSwitch
                    checked={state.archived}
                    onChange={(e: any) =>
                      runIfEnabled(() => handleSwitchChange(e))
                    }
                    name='archived'
                    disabled={isInteractionDisabled}
                  />
                  <span>Include Archived Data</span>
                  <ModuleContentTooltip
                    title={
                      <p className='module-header-tooltip-text'>
                        Archived data is data that has been removed from active
                        watchlists. It surfaces entities that were previously
                        listed, which is useful for historical reference or to
                        see how a record has changed over time.
                      </p>
                    }
                    placement='top'
                    arrow
                    componentsProps={switchInfoTooltipProps}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 17, cursor: 'help' }} />
                  </ModuleContentTooltip>
                </div>
                <button
                  type='button'
                  className={`entity-screening-date-btn${
                    entityDateLabel ? ' has-value' : ''
                  }`}
                  onClick={() => runIfEnabled(() => handleOpenDate())}
                  disabled={isInteractionDisabled}
                >
                  <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />
                  {entityDateLabel || 'As of date'}
                  {entityDateLabel && (
                    <span
                      className='entity-screening-date-clear'
                      role='button'
                      aria-label='Clear date filter'
                      onClick={(e) => {
                        e.stopPropagation();
                        runIfEnabled(() => handleClearDates());
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 15 }} />
                    </span>
                  )}
                </button>
                <button
                  type='button'
                  className='entity-screening-clear-btn'
                  onClick={handleEntityPageReset}
                  disabled={isInteractionDisabled}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                  Clear
                </button>
                <button
                  type='button'
                  className='entity-screening-run-btn'
                  onClick={() => runIfEnabled(() => handleSubmit(page))}
                  disabled={isInteractionDisabled}
                >
                  {isInteractionDisabled ? (
                    <LockIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <SearchIcon sx={{ fontSize: 20 }} />
                  )}
                  Run Screening
                </button>
              </div>
            )}
          </div>
          <SaveFilterModal
            open={saveFilterModalOpen}
            onClose={() => setSaveFilterModalOpen(false)}
            onSave={handleSaveFilter}
            summary={saveFilterSummary}
            existingNames={savedEntityFilters.map(
              (filt: any) => filt.filterName,
            )}
          />
        </>
      )}

      {isProductSearch && (
        <>
          <div className='entity-screening-hero'>
            <div>
              <h1 className='entity-screening-title'>{direction} Screening</h1>
              <p className='entity-screening-subtitle'>
                Screen products by HTS code, description, and trade route
              </p>
            </div>
            <div className='entity-screening-hero-actions'>
              <ModuleContentTooltip
                title={
                  <p className='module-header-tooltip-text'>
                    Enter a product name or HTS code, add country information,
                    and refine the screen with list and HTS code filters.
                  </p>
                }
                placement='bottom'
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      display: 'flex',
                      padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      alignSelf: 'stretch',
                      borderRadius: 'var(--radius-md, 8px)',
                      background: 'var(--Background-200, var(--bg-colors))',
                      color: 'var(--text-prim25)',
                      maxWidth: '400px',
                      '& .MuiTooltip-arrow': {
                        color: 'var(--Background-200, var(--bg-colors))',
                      },
                    },
                  },
                }}
              >
                <button type='button' className='entity-screening-help-btn'>
                  <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                  Help
                </button>
              </ModuleContentTooltip>
              {!isWhiteLabel && (
                <button
                  type='button'
                  className='entity-screening-action-btn'
                  onClick={() => runIfEnabled(() => setTicketNameOpen(true))}
                  disabled={
                    isInteractionDisabled ||
                    !productHasQuery ||
                    !screeningResultsTotal
                  }
                >
                  <SaveOutlinedIcon sx={{ fontSize: 18 }} />
                  Save
                </button>
              )}
            </div>
          </div>

          {isImportProduct && (
            <div className='entity-screening-segmented product-trade-tabs'>
              {importTradeTabs.map((tab) => (
                <button
                  key={tab.value}
                  type='button'
                  className={activeTradeType === tab.value ? 'active' : ''}
                  onClick={() =>
                    runIfEnabled(() => setTradeType && setTradeType(tab.value))
                  }
                  disabled={isInteractionDisabled}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          <div className='entity-screening-form-panel product-screening-redesign'>
            <div
              className={`entity-screening-input-grid product-screening-input-grid${
                direction === 'Import' ? ' import-mode' : ''
              }${direction === 'Export' ? ' export-mode' : ''}`}
            >
              <label className='entity-screening-field'>
                <span>HTS Code</span>
                <div className='screening-input-field-container entity-screening-input'>
                  <ArticleOutlinedIcon sx={{ fontSize: 20 }} />
                  <input
                    id='hsCode'
                    type='text'
                    placeholder='Required for AI evaluation'
                    className='screening-input-field-content'
                    name='hsCode'
                    value={inputs.hsCode}
                    onChange={(e) => handleChange(e)}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter' && canRunProductScreen) {
                        handleSubmit(page);
                      }
                    }}
                    disabled={isInteractionDisabled}
                  />
                  {inputs.hsCode && (
                    <span
                      className='entity-screening-input-clear'
                      role='button'
                      aria-label='Clear HTS code'
                      onClick={() =>
                        runIfEnabled(() => handleClearInput('hsCode'))
                      }
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </span>
                  )}
                </div>
              </label>

              <label className='entity-screening-field product-screening-desc-field'>
                <span>Product Name or Description</span>
                <div className='screening-input-field-container entity-screening-input'>
                  <SearchIcon sx={{ fontSize: 20 }} />
                  <input
                    id='desc'
                    type='text'
                    placeholder='Enter product name or description'
                    className='screening-input-field-content'
                    name='desc'
                    value={inputs.desc}
                    onChange={(e) => handleChange(e)}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter' && canRunProductScreen) {
                        handleSubmit(page);
                      }
                    }}
                    disabled={isInteractionDisabled}
                  />
                  {inputs.desc && (
                    <span
                      className='entity-screening-input-clear'
                      role='button'
                      aria-label='Clear product description'
                      onClick={() =>
                        runIfEnabled(() => handleClearInput('desc'))
                      }
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </span>
                  )}
                </div>
              </label>

              {direction !== 'Export' && (
                <label className='entity-screening-field'>
                  <span>Country of Origin</span>
                  {renderProductCountryDropdown(
                    'origin',
                    'Required for AI evaluation',
                    'Clear origin country',
                  )}
                </label>
              )}

              {direction !== 'Import' && (
                <label className='entity-screening-field'>
                  <span>Country of Destination</span>
                  {renderProductCountryDropdown(
                    'destination',
                    'Required for AI evaluation',
                    'Clear destination country',
                  )}
                </label>
              )}
            </div>

            <section className='entity-screening-parameters-panel'>
              <button
                type='button'
                className={`entity-screening-parameters-header${
                  productParametersOpen ? ' open' : ''
                }`}
                onClick={() => setProductParametersOpen((prev) => !prev)}
              >
                <span>
                  <FilterListIcon sx={{ fontSize: 20 }} />
                  <strong>Screening Parameters</strong>
                  {(totalAppliedParameters > 0 || !productParametersOpen) && (
                    <em>
                      {totalAppliedParameters
                        ? `${totalAppliedParameters} filters applied`
                        : 'Defaults to all list and HTS code filters'}
                    </em>
                  )}
                </span>
                <ExpandMoreOutlinedIcon
                  className={productParametersOpen ? 'open' : ''}
                  sx={{ fontSize: 24 }}
                />
              </button>
              {productParametersOpen && (
                <div className='entity-screening-parameters-body product-screening-parameters-body'>
                  <div className='product-screening-filter-section-header'>
                    <span>
                      <FilterListIcon sx={{ fontSize: 18 }} />
                      List Filters
                    </span>
                  </div>
                  {renderProductSavedFilterBar('list')}
                  <p className='entity-screening-parameters-note'>
                    <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                    Selections cascade left-to-right. Choosing a lens narrows
                    the available countries, which narrows the agencies, and so
                    on.
                  </p>
                  {renderProductParameterGrid(
                    productListParameterConfigs,
                    productListParameterGridRef,
                  )}
                  {!isNonUsImport && (
                    <div className='product-screening-hts-row'>
                      <div className='product-screening-filter-section-header'>
                        <span>
                          <ArticleOutlinedIcon sx={{ fontSize: 18 }} />
                          HTS Code Filters
                        </span>
                      </div>
                      {renderProductSavedFilterBar('code')}
                      {renderProductParameterGrid(
                        productCodeParameterConfigs,
                        productCodeParameterGridRef,
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>

            <div className='entity-screening-controls'>
              {/* Entity Resolution toggle hidden from Import/Export screening UI
                  per request — state.fuzzy still exists and is still applied
                  in the search query, this just removes the visible control. */}
              {/* <div className='entity-screening-switch-group'>
                <CustomSwitch
                  checked={state.fuzzy}
                  onChange={(e: any) =>
                    runIfEnabled(() => handleSwitchChange(e))
                  }
                  name='fuzzy'
                  disabled={isInteractionDisabled}
                />
                <span>Entity Resolution</span>
                <ModuleContentTooltip
                  title={
                    <p className='module-header-tooltip-text'>
                      Entity Resolution links near-matches and minor variations
                      instead of exact matches only.
                    </p>
                  }
                  placement='top'
                  arrow
                  componentsProps={switchInfoTooltipProps}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 17, cursor: 'help' }} />
                </ModuleContentTooltip>
              </div> */}
              <div className='entity-screening-switch-group'>
                <CustomSwitch
                  checked={state.archived}
                  onChange={(e: any) =>
                    runIfEnabled(() => handleSwitchChange(e))
                  }
                  name='archived'
                  disabled={isInteractionDisabled}
                />
                <span>Include Archived Data</span>
                <ModuleContentTooltip
                  title={
                    <p className='module-header-tooltip-text'>
                      Archived data surfaces records that have been removed from
                      active data sets for historical reference.
                    </p>
                  }
                  placement='top'
                  arrow
                  componentsProps={switchInfoTooltipProps}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 17, cursor: 'help' }} />
                </ModuleContentTooltip>
              </div>
              <button
                type='button'
                className={`entity-screening-date-btn${
                  screeningDateLabel ? ' has-value' : ''
                }`}
                onClick={() => runIfEnabled(() => handleOpenDate())}
                disabled={isInteractionDisabled}
              >
                <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />
                {screeningDateLabel || 'As of date'}
              </button>
              <button
                type='button'
                className='entity-screening-clear-btn'
                onClick={() => runIfEnabled(() => handleClear())}
                disabled={isInteractionDisabled}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
                Clear
              </button>
              <button
                type='button'
                className='entity-screening-run-btn'
                onClick={() => runIfEnabled(() => handleSubmit(page))}
                disabled={!canRunProductScreen}
              >
                {isInteractionDisabled ? (
                  <LockIcon sx={{ fontSize: 18 }} />
                ) : (
                  <SearchIcon sx={{ fontSize: 20 }} />
                )}
                Run Screening
              </button>
            </div>
          </div>
          <SaveFilterModal
            open={productSaveFilterModalOpen}
            onClose={() => setProductSaveFilterModalOpen(false)}
            onSave={handleSaveProductFilter}
            summary={
              productSaveFilterTarget === 'list'
                ? productListSaveSummary
                : productCodeSaveSummary
            }
            existingNames={
              productSaveFilterTarget === 'list'
                ? savedProductListFilters.map((filt: any) => filt.filterName)
                : savedProductCodeFilters.map((filt: any) => filt.filterName)
            }
          />
        </>
      )}

      {!usesScreeningRedesign && (
        <>
          <div className='screening-inputs-list'>
            <div className='screening-input-row'>
              <div className='screening-label-input'>
                <div style={{ width: '-webkit-fill-available' }}>
                  {type === 'Entities' ? (
                    <div
                      className='screening-input-group'
                      style={{ marginTop: 0 }}
                    >
                      <div className='screening-input-minors'>
                        <div
                          className='screening-input-field-container'
                          style={{
                            width: direction === 'Import' ? '100%' : '50%',
                          }}
                        >
                          <input
                            id='primary'
                            type='text'
                            placeholder='Required: Enter Person or Company Name'
                            className='screening-input-field-content'
                            name='primary'
                            value={inputs.primary}
                            onChange={(e) => handleChange(e)}
                            onKeyUp={(e) => {
                              if (e.key === 'Enter' && !isInteractionDisabled) {
                                handleSubmit(page);
                              }
                            }}
                            disabled={isInteractionDisabled}
                          />
                          <svg
                            onClick={() => handleClearInput('primary')}
                            style={{ cursor: 'pointer' }}
                            xmlns='http://www.w3.org/2000/svg'
                            width='16'
                            height='16'
                            viewBox='0 0 16 16'
                            fill='none'
                          >
                            <path
                              d='M12 4L4 12M4 4L12 12'
                              stroke='var(--card-text)'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </div>
                        <div
                          className='screening-input-field-container'
                          style={{ width: '50%' }}
                        >
                          <input
                            id='record'
                            type='text'
                            placeholder='Alternative: Enter BITE Record ID'
                            className='screening-input-field-content'
                            name='record'
                            value={inputs.record}
                            onChange={(e) => handleChange(e)}
                            onKeyUp={(e) => {
                              if (e.key === 'Enter' && !isInteractionDisabled) {
                                handleSubmit(page);
                              }
                            }}
                          />
                          <svg
                            onClick={() => handleClearInput('record')}
                            style={{ cursor: 'pointer' }}
                            xmlns='http://www.w3.org/2000/svg'
                            width='16'
                            height='16'
                            viewBox='0 0 16 16'
                            fill='none'
                          >
                            <path
                              d='M12 4L4 12M4 4L12 12'
                              stroke='var(--card-text)'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </div>
                      </div>
                      <div className='screening-input-minors'>
                        {/* Origin Country */}
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '50%',
                            gap: '3px',
                          }}
                        >
                          <Autocomplete
                            options={screeningCountries as COpt[]}
                            getOptionLabel={(option: any) =>
                              displayLabel(option)
                            }
                            isOptionEqualToValue={(opt: any, val: any) =>
                              (Boolean(getIso(opt)) &&
                                getIso(opt) === getIso(val)) ||
                              getName(opt) === getName(val)
                            }
                            clearOnEscape
                            clearIcon={
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='16'
                                height='16'
                                viewBox='0 0 16 16'
                                fill='none'
                              >
                                <path
                                  d='M12 4L4 12M4 4L12 12'
                                  stroke='var(--card-text)'
                                  strokeWidth='2'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                />
                              </svg>
                            }
                            componentsProps={{
                              popper: {
                                sx: {
                                  '& .MuiAutocomplete-option': {
                                    color:
                                      'var(--text-primary-white) !important',
                                  },
                                }, // Higher z-index to sit above sidebar/modals
                              },
                              paper: {
                                sx: {
                                  color: 'var(--text-primary-white) !important',
                                },
                              },
                            }}
                            sx={{
                              background:
                                'var(--Background-200, var(--bg-colors))',
                              color: 'var(--text-prim25)',
                              borderRadius: '8px',
                              height: '45px',
                              '& .MuiAutocomplete-input': {
                                color:
                                  'var(--Text-Main, var(--text-primary-white))',
                                fontFamily: 'Inter',
                                fontSize: '14px',
                                fontWeight: 400,
                                lineHeight: '24px',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                border: 'none',
                                background:
                                  'var(--Background-200,var(--bg-colors))',
                                borderRadius: '8px',
                                height: '45px',
                              },
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                border: '1px solid var(--bg-colors)',
                                height: '45px',
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'var(--bg-secondary)',
                                },
                                color: 'var(--text-primary-white)',
                                '&.Mui-focused': {
                                  color: 'var(--text-primary-white)',
                                },
                              },
                              '& .MuiSvgIcon-root': {
                                color: 'var(--card-text)',
                              },
                            }}
                            onChange={(_: any, newValue: string) =>
                              handleCountryChange(newValue)
                            }
                            renderInput={(params: any) => (
                              <TextField
                                {...params}
                                placeholder='Select Country of Origin'
                              />
                            )}
                            value={findCountryOption(inputs.country)}
                          />
                        </div>
                        <div
                          className='screening-input-field-container'
                          style={{ width: '50%' }}
                        >
                          <input
                            id='address'
                            type='text'
                            placeholder='Optional: Enter Address'
                            className='screening-input-field-content'
                            name='address'
                            value={inputs.address}
                            onChange={(e) => handleChange(e)}
                            onKeyUp={(e) => {
                              if (e.key === 'Enter' && !isInteractionDisabled) {
                                handleSubmit(page);
                              }
                            }}
                          />
                          <svg
                            onClick={() => handleClearInput('address')}
                            style={{ cursor: 'pointer' }}
                            xmlns='http://www.w3.org/2000/svg'
                            width='16'
                            height='16'
                            viewBox='0 0 16 16'
                            fill='none'
                          >
                            <path
                              d='M12 4L4 12M4 4L12 12'
                              stroke='var(--card-text)'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ) : type === 'Products' ? (
                    <div
                      className='screening-input-group'
                      style={{ marginTop: 0 }}
                    >
                      <div className='screening-input-minors'>
                        <div
                          className='screening-input-field-container'
                          style={{ width: '35%' }}
                        >
                          <input
                            id='hsCode'
                            type='text'
                            placeholder='Required for AI: Enter HTS Code'
                            className='screening-input-field-content'
                            name='hsCode'
                            value={inputs.hsCode}
                            onChange={(e) => handleChange(e)}
                            onKeyUp={(e) => {
                              if (e.key === 'Enter' && !isInteractionDisabled) {
                                handleSubmit(page);
                              }
                            }}
                          />
                          <svg
                            onClick={() => handleClearInput('hsCode')}
                            style={{ cursor: 'pointer' }}
                            xmlns='http://www.w3.org/2000/svg'
                            width='16'
                            height='16'
                            viewBox='0 0 16 16'
                            fill='none'
                          >
                            <path
                              d='M12 4L4 12M4 4L12 12'
                              stroke='var(--card-text)'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </div>
                        <div
                          className='screening-input-field-container'
                          style={{ width: '65%' }}
                        >
                          <input
                            id='desc'
                            type='text'
                            placeholder='Enter Product Name or Description'
                            className='screening-input-field-content'
                            name='desc'
                            value={inputs.desc}
                            onChange={(e) => handleChange(e)}
                            onKeyUp={(e) => {
                              if (e.key === 'Enter' && !isInteractionDisabled) {
                                handleSubmit(page);
                              }
                            }}
                          />
                          <svg
                            onClick={() => handleClearInput('desc')}
                            style={{ cursor: 'pointer' }}
                            xmlns='http://www.w3.org/2000/svg'
                            width='16'
                            height='16'
                            viewBox='0 0 16 16'
                            fill='none'
                          >
                            <path
                              d='M12 4L4 12M4 4L12 12'
                              stroke='var(--card-text)'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </div>
                      </div>
                      <div className='screening-input-minors'>
                        {direction !== 'Export' &&
                          renderProductCountryDropdown(
                            'origin',
                            'Required for AI Eval: Select Country of Origin',
                            'Clear origin country',
                            direction === 'Import' ? '100%' : '50%',
                          )}
                        {direction !== 'Import' &&
                          renderProductCountryDropdown(
                            'destination',
                            'Required for AI Eval: Select Country of Destination',
                            'Clear destination country',
                            direction === 'Export' ? '100%' : '50%',
                          )}
                      </div>
                    </div>
                  ) : (
                    <div className='screening-input-field-container'>
                      <input
                        id='cyber'
                        type='text'
                        className='screening-input-field-content'
                        placeholder='Required: Enter Value'
                        name='cyber'
                        value={inputs.cyber}
                        onChange={(e) => handleChange(e)}
                        onKeyUp={(e) => {
                          if (e.key === 'Enter' && !isInteractionDisabled) {
                            handleSubmit(page);
                          }
                        }}
                      />
                      <svg
                        onClick={() => handleClearInput('cyber')}
                        style={{ cursor: 'pointer' }}
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                      >
                        <path
                          d='M12 4L4 12M4 4L12 12'
                          stroke='var(--card-text)'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className='screening-filters-cont'>
            <div className='screening-filters-group'>
              {type !== 'Cyber' && (
                <div
                  style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                >
                  <ModuleContentTooltip
                    title={
                      <p className='module-header-tooltip-text'>
                        Utilize BITE Lens and select dates for when the data was
                        added to narrow down your screening results.
                      </p>
                    }
                    placement='top'
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          display: 'flex',
                          padding:
                            'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          alignSelf: 'stretch',
                          borderRadius: 'var(--radius-md, 8px)',
                          background: 'var(--Background-200, var(--bg-colors))',
                          color: 'var(--text-prim25)',
                          maxWidth: '400px',
                          '& .MuiTooltip-arrow': {
                            color: 'var(--Background-200, var(--bg-colors))',
                          },
                        },
                      },
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                    >
                      <path
                        d='M9 8.25C8.80109 8.25 8.61033 8.32902 8.46967 8.46967C8.32902 8.61032 8.25 8.80109 8.25 9V12C8.25 12.1989 8.32902 12.3897 8.46967 12.5303C8.61033 12.671 8.80109 12.75 9 12.75C9.19892 12.75 9.38968 12.671 9.53033 12.5303C9.67099 12.3897 9.75 12.1989 9.75 12V9C9.75 8.80109 9.67099 8.61032 9.53033 8.46967C9.38968 8.32902 9.19892 8.25 9 8.25ZM9.285 5.31C9.10241 5.23499 8.8976 5.23499 8.715 5.31C8.62294 5.3457 8.53883 5.39922 8.4675 5.4675C8.40126 5.5404 8.34799 5.62411 8.31 5.715C8.26802 5.80401 8.24747 5.90162 8.25 6C8.24943 6.0987 8.26835 6.19655 8.30568 6.28793C8.343 6.37931 8.39799 6.46242 8.4675 6.5325C8.5404 6.59875 8.62412 6.65202 8.715 6.69C8.82863 6.73668 8.95198 6.75474 9.07421 6.74258C9.19645 6.73043 9.31383 6.68844 9.41604 6.6203C9.51825 6.55216 9.60215 6.45996 9.66039 6.3518C9.71862 6.24364 9.74939 6.12284 9.75 6C9.74724 5.80142 9.66955 5.61123 9.5325 5.4675C9.46118 5.39922 9.37707 5.3457 9.285 5.31ZM9 1.5C7.51664 1.5 6.0666 1.93987 4.83323 2.76398C3.59986 3.58809 2.63856 4.75943 2.07091 6.12987C1.50325 7.50032 1.35472 9.00832 1.64411 10.4632C1.9335 11.918 2.64781 13.2544 3.6967 14.3033C4.7456 15.3522 6.08197 16.0665 7.53683 16.3559C8.99168 16.6453 10.4997 16.4968 11.8701 15.9291C13.2406 15.3614 14.4119 14.4001 15.236 13.1668C16.0601 11.9334 16.5 10.4834 16.5 9C16.5 8.01509 16.306 7.03982 15.9291 6.12987C15.5522 5.21993 14.9997 4.39314 14.3033 3.6967C13.6069 3.00026 12.7801 2.44781 11.8701 2.0709C10.9602 1.69399 9.98492 1.5 9 1.5ZM9 15C7.81332 15 6.65328 14.6481 5.66658 13.9888C4.67989 13.3295 3.91085 12.3925 3.45673 11.2961C3.0026 10.1997 2.88378 8.99334 3.11529 7.82946C3.3468 6.66557 3.91825 5.59647 4.75736 4.75736C5.59648 3.91824 6.66558 3.3468 7.82946 3.11529C8.99335 2.88378 10.1997 3.0026 11.2961 3.45672C12.3925 3.91085 13.3295 4.67988 13.9888 5.66658C14.6481 6.65327 15 7.81331 15 9C15 10.5913 14.3679 12.1174 13.2426 13.2426C12.1174 14.3679 10.5913 15 9 15Z'
                        fill='var(--alternative-text-color)'
                      />
                    </svg>
                  </ModuleContentTooltip>
                  <button
                    className={
                      isInteractionDisabled
                        ? 'btn-new-automate-disabled'
                        : 'btn-new-automate'
                    }
                    onClick={() => runIfEnabled(() => openFilters())}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      {isInteractionDisabled && <LockIcon fontSize='small' />}
                      Set List Filters
                    </div>
                  </button>
                  {type === 'Products' && (
                    <button
                      className={
                        isInteractionDisabled
                          ? 'btn-new-automate-disabled'
                          : 'btn-new-automate'
                      }
                      onClick={() => runIfEnabled(() => openFiltersCode())}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        {isInteractionDisabled && <LockIcon fontSize='small' />}
                        Set HTS Code Filters
                      </div>
                    </button>
                  )}
                  {state.lists.length > 0 && (
                    <SelectionsBadge selections={state.lists} />
                  )}
                </div>
              )}

              <div
                className='filter-select-box'
                onClick={() => runIfEnabled(() => handleOpenDate())}
                style={{
                  opacity: isInteractionDisabled ? 0.6 : 1,
                  cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='18'
                  height='18'
                  viewBox='0 0 18 18'
                  fill='none'
                >
                  <path
                    d='M15.75 7.5H2.25M12 1.5V4.5M6 1.5V4.5M5.85 16.5H12.15C13.4101 16.5 14.0402 16.5 14.5215 16.2548C14.9448 16.039 15.289 15.6948 15.5048 15.2715C15.75 14.7902 15.75 14.1601 15.75 12.9V6.6C15.75 5.33988 15.75 4.70982 15.5048 4.22852C15.289 3.80516 14.9448 3.46095 14.5215 3.24524C14.0402 3 13.4101 3 12.15 3H5.85C4.58988 3 3.95982 3 3.47852 3.24524C3.05516 3.46095 2.71095 3.80516 2.49524 4.22852C2.25 4.70982 2.25 5.33988 2.25 6.6V12.9C2.25 14.1601 2.25 14.7902 2.49524 15.2715C2.71095 15.6948 3.05516 16.039 3.47852 16.2548C3.95982 16.5 4.58988 16.5 5.85 16.5Z'
                    stroke='var(--text-primary)'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                <h2 className='filter-select-box-text'>Date</h2>
                {state.dateRange && state.dateRange.length > 0 && (
                  <SelectionsBadge selections={['']} />
                )}
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M9.17019 6.99981C8.98394 7.18717 8.87939 7.44062 8.87939 7.70481C8.87939 7.96899 8.98394 8.22244 9.17019 8.40981L12.7102 11.9998L9.17019 15.5398C8.98394 15.7272 8.87939 15.9806 8.87939 16.2448C8.87939 16.509 8.98394 16.7624 9.17019 16.9498C9.26315 17.0435 9.37375 17.1179 9.49561 17.1687C9.61747 17.2195 9.74818 17.2456 9.88019 17.2456C10.0122 17.2456 10.1429 17.2195 10.2648 17.1687C10.3866 17.1179 10.4972 17.0435 10.5902 16.9498L14.8302 12.7098C14.9239 12.6168 14.9983 12.5062 15.0491 12.3844C15.0998 12.2625 15.126 12.1318 15.126 11.9998C15.126 11.8678 15.0998 11.7371 15.0491 11.6152C14.9983 11.4934 14.9239 11.3828 14.8302 11.2898L10.5902 6.99981C10.4972 6.90608 10.3866 6.83169 10.2648 6.78092C10.1429 6.73015 10.0122 6.70401 9.88019 6.70401C9.74818 6.70401 9.61747 6.73015 9.49561 6.78092C9.37375 6.83169 9.26315 6.90608 9.17019 6.99981Z'
                    fill='var(--text-primary)'
                  />
                </svg>
              </div>

              <div className='filter-select-tooltip'>
                <h2>Entity Resolution?</h2>
                <ModuleContentTooltip
                  title={
                    <p className='module-header-tooltip-text'>
                      Entity Resolution is the ability to link records that have
                      the same data in real life but may have a different
                      spelling or formatting between the data sets being
                      compared.
                    </p>
                  }
                  placement='top'
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        display: 'flex',
                        padding:
                          'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        alignSelf: 'stretch',
                        borderRadius: 'var(--radius-md, 8px)',
                        background: 'var(--Background-200, var(--bg-colors))',
                        color: 'var(--text-prim25)',
                        maxWidth: '400px',
                        '& .MuiTooltip-arrow': {
                          color: 'var(--Background-200, var(--bg-colors))',
                        },
                      },
                    },
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 18 18'
                    fill='none'
                  >
                    <path
                      d='M9 8.25C8.80109 8.25 8.61033 8.32902 8.46967 8.46967C8.32902 8.61032 8.25 8.80109 8.25 9V12C8.25 12.1989 8.32902 12.3897 8.46967 12.5303C8.61033 12.671 8.80109 12.75 9 12.75C9.19892 12.75 9.38968 12.671 9.53033 12.5303C9.67099 12.3897 9.75 12.1989 9.75 12V9C9.75 8.80109 9.67099 8.61032 9.53033 8.46967C9.38968 8.32902 9.19892 8.25 9 8.25ZM9.285 5.31C9.10241 5.23499 8.8976 5.23499 8.715 5.31C8.62294 5.3457 8.53883 5.39922 8.4675 5.4675C8.40126 5.5404 8.34799 5.62411 8.31 5.715C8.26802 5.80401 8.24747 5.90162 8.25 6C8.24943 6.0987 8.26835 6.19655 8.30568 6.28793C8.343 6.37931 8.39799 6.46242 8.4675 6.5325C8.5404 6.59875 8.62412 6.65202 8.715 6.69C8.82863 6.73668 8.95198 6.75474 9.07421 6.74258C9.19645 6.73043 9.31383 6.68844 9.41604 6.6203C9.51825 6.55216 9.60215 6.45996 9.66039 6.3518C9.71862 6.24364 9.74939 6.12284 9.75 6C9.74724 5.80142 9.66955 5.61123 9.5325 5.4675C9.46118 5.39922 9.37707 5.3457 9.285 5.31ZM9 1.5C7.51664 1.5 6.0666 1.93987 4.83323 2.76398C3.59986 3.58809 2.63856 4.75943 2.07091 6.12987C1.50325 7.50032 1.35472 9.00832 1.64411 10.4632C1.9335 11.918 2.64781 13.2544 3.6967 14.3033C4.7456 15.3522 6.08197 16.0665 7.53683 16.3559C8.99168 16.6453 10.4997 16.4968 11.8701 15.9291C13.2406 15.3614 14.4119 14.4001 15.236 13.1668C16.0601 11.9334 16.5 10.4834 16.5 9C16.5 8.01509 16.306 7.03982 15.9291 6.12987C15.5522 5.21993 14.9997 4.39314 14.3033 3.6967C13.6069 3.00026 12.7801 2.44781 11.8701 2.0709C10.9602 1.69399 9.98492 1.5 9 1.5ZM9 15C7.81332 15 6.65328 14.6481 5.66658 13.9888C4.67989 13.3295 3.91085 12.3925 3.45673 11.2961C3.0026 10.1997 2.88378 8.99334 3.11529 7.82946C3.3468 6.66557 3.91825 5.59647 4.75736 4.75736C5.59648 3.91824 6.66558 3.3468 7.82946 3.11529C8.99335 2.88378 10.1997 3.0026 11.2961 3.45672C12.3925 3.91085 13.3295 4.67988 13.9888 5.66658C14.6481 6.65327 15 7.81331 15 9C15 10.5913 14.3679 12.1174 13.2426 13.2426C12.1174 14.3679 10.5913 15 9 15Z'
                      fill='var(--alternative-text-color)'
                    />
                  </svg>
                </ModuleContentTooltip>
              </div>
              <CustomLabel
                control={
                  <CustomSwitch
                    checked={state.fuzzy}
                    onChange={(e: any) =>
                      runIfEnabled(() => handleSwitchChange(e))
                    }
                    name='fuzzy'
                    disabled={isInteractionDisabled}
                  />
                }
                label={state.fuzzy ? 'Yes' : 'No'}
              />

              <div className='filter-select-tooltip'>
                <h2>Include Archived Data?</h2>
                <ModuleContentTooltip
                  title={
                    <p className='module-header-tooltip-text'>
                      Archived data is data that has been removed from the
                      system. This is useful for historical refrence or to show
                      how data has changed over time.
                    </p>
                  }
                  placement='top'
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        display: 'flex',
                        padding:
                          'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        alignSelf: 'stretch',
                        borderRadius: 'var(--radius-md, 8px)',
                        background: 'var(--Background-200, var(--bg-colors))',
                        color: 'var(--text-prim25)',
                        maxWidth: '400px',
                        '& .MuiTooltip-arrow': {
                          color: 'var(--Background-200, var(--bg-colors))',
                        },
                      },
                    },
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 18 18'
                    fill='none'
                  >
                    <path
                      d='M9 8.25C8.80109 8.25 8.61033 8.32902 8.46967 8.46967C8.32902 8.61032 8.25 8.80109 8.25 9V12C8.25 12.1989 8.32902 12.3897 8.46967 12.5303C8.61033 12.671 8.80109 12.75 9 12.75C9.19892 12.75 9.38968 12.671 9.53033 12.5303C9.67099 12.3897 9.75 12.1989 9.75 12V9C9.75 8.80109 9.67099 8.61032 9.53033 8.46967C9.38968 8.32902 9.19892 8.25 9 8.25ZM9.285 5.31C9.10241 5.23499 8.8976 5.23499 8.715 5.31C8.62294 5.3457 8.53883 5.39922 8.4675 5.4675C8.40126 5.5404 8.34799 5.62411 8.31 5.715C8.26802 5.80401 8.24747 5.90162 8.25 6C8.24943 6.0987 8.26835 6.19655 8.30568 6.28793C8.343 6.37931 8.39799 6.46242 8.4675 6.5325C8.5404 6.59875 8.62412 6.65202 8.715 6.69C8.82863 6.73668 8.95198 6.75474 9.07421 6.74258C9.19645 6.73043 9.31383 6.68844 9.41604 6.6203C9.51825 6.55216 9.60215 6.45996 9.66039 6.3518C9.71862 6.24364 9.74939 6.12284 9.75 6C9.74724 5.80142 9.66955 5.61123 9.5325 5.4675C9.46118 5.39922 9.37707 5.3457 9.285 5.31ZM9 1.5C7.51664 1.5 6.0666 1.93987 4.83323 2.76398C3.59986 3.58809 2.63856 4.75943 2.07091 6.12987C1.50325 7.50032 1.35472 9.00832 1.64411 10.4632C1.9335 11.918 2.64781 13.2544 3.6967 14.3033C4.7456 15.3522 6.08197 16.0665 7.53683 16.3559C8.99168 16.6453 10.4997 16.4968 11.8701 15.9291C13.2406 15.3614 14.4119 14.4001 15.236 13.1668C16.0601 11.9334 16.5 10.4834 16.5 9C16.5 8.01509 16.306 7.03982 15.9291 6.12987C15.5522 5.21993 14.9997 4.39314 14.3033 3.6967C13.6069 3.00026 12.7801 2.44781 11.8701 2.0709C10.9602 1.69399 9.98492 1.5 9 1.5ZM9 15C7.81332 15 6.65328 14.6481 5.66658 13.9888C4.67989 13.3295 3.91085 12.3925 3.45673 11.2961C3.0026 10.1997 2.88378 8.99334 3.11529 7.82946C3.3468 6.66557 3.91825 5.59647 4.75736 4.75736C5.59648 3.91824 6.66558 3.3468 7.82946 3.11529C8.99335 2.88378 10.1997 3.0026 11.2961 3.45672C12.3925 3.91085 13.3295 4.67988 13.9888 5.66658C14.6481 6.65327 15 7.81331 15 9C15 10.5913 14.3679 12.1174 13.2426 13.2426C12.1174 14.3679 10.5913 15 9 15Z'
                      fill='var(--alternative-text-color)'
                    />
                  </svg>
                </ModuleContentTooltip>
              </div>
              <CustomLabel
                control={
                  <CustomSwitch
                    checked={state.archived}
                    onChange={(e: any) =>
                      runIfEnabled(() => handleSwitchChange(e))
                    }
                    name='archived'
                    disabled={isInteractionDisabled}
                  />
                }
                label={state.archived ? 'Yes' : 'No'}
              />

              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  marginLeft: 'auto',
                }}
              >
                <ModuleContentTooltip
                  title={
                    type === 'Entities' ? (
                      <p className='module-header-tooltip-text'>
                        Enter a person or company name to be screened on the
                        BITE data lists. <br />
                        <br /> you may enter a country or address to increase
                        the accuracy of your evaluation.
                      </p>
                    ) : (
                      <p className='module-header-tooltip-text'>
                        Enter a product name or HTS Code to be screened on the
                        BITE data lists, you may enter country information for a
                        more accurate evaluation of your screen. <br />
                        <br /> If both HTS Code and Descritption are entered the
                        HTS Code will be searched. <br />
                        <br /> If you do not know your HTS Code you may use our{' '}
                        <Link to='/bite-classify'>
                          <Button sx={{ color: '#4895EF', padding: 0 }}>
                            BITE Classify Tool
                          </Button>
                        </Link>
                      </p>
                    )
                  }
                  placement='top'
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        display: 'flex',
                        padding:
                          'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        alignSelf: 'stretch',
                        borderRadius: 'var(--radius-md, 8px)',
                        background: 'var(--Background-200, var(--bg-colors))',
                        color: 'var(--text-prim25)',
                        maxWidth: '400px',
                        '& .MuiTooltip-arrow': {
                          color: 'var(--Background-200, var(--bg-colors))',
                        },
                      },
                    },
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 18 18'
                    fill='none'
                  >
                    <path
                      d='M9 8.25C8.80109 8.25 8.61033 8.32902 8.46967 8.46967C8.32902 8.61032 8.25 8.80109 8.25 9V12C8.25 12.1989 8.32902 12.3897 8.46967 12.5303C8.61033 12.671 8.80109 12.75 9 12.75C9.19892 12.75 9.38968 12.671 9.53033 12.5303C9.67099 12.3897 9.75 12.1989 9.75 12V9C9.75 8.80109 9.67099 8.61032 9.53033 8.46967C9.38968 8.32902 9.19892 8.25 9 8.25ZM9.285 5.31C9.10241 5.23499 8.8976 5.23499 8.715 5.31C8.62294 5.3457 8.53883 5.39922 8.4675 5.4675C8.40126 5.5404 8.34799 5.62411 8.31 5.715C8.26802 5.80401 8.24747 5.90162 8.25 6C8.24943 6.0987 8.26835 6.19655 8.30568 6.28793C8.343 6.37931 8.39799 6.46242 8.4675 6.5325C8.5404 6.59875 8.62412 6.65202 8.715 6.69C8.82863 6.73668 8.95198 6.75474 9.07421 6.74258C9.19645 6.73043 9.31383 6.68844 9.41604 6.6203C9.51825 6.55216 9.60215 6.45996 9.66039 6.3518C9.71862 6.24364 9.74939 6.12284 9.75 6C9.74724 5.80142 9.66955 5.61123 9.5325 5.4675C9.46118 5.39922 9.37707 5.3457 9.285 5.31ZM9 1.5C7.51664 1.5 6.0666 1.93987 4.83323 2.76398C3.59986 3.58809 2.63856 4.75943 2.07091 6.12987C1.50325 7.50032 1.35472 9.00832 1.64411 10.4632C1.9335 11.918 2.64781 13.2544 3.6967 14.3033C4.7456 15.3522 6.08197 16.0665 7.53683 16.3559C8.99168 16.6453 10.4997 16.4968 11.8701 15.9291C13.2406 15.3614 14.4119 14.4001 15.236 13.1668C16.0601 11.9334 16.5 10.4834 16.5 9C16.5 8.01509 16.306 7.03982 15.9291 6.12987C15.5522 5.21993 14.9997 4.39314 14.3033 3.6967C13.6069 3.00026 12.7801 2.44781 11.8701 2.0709C10.9602 1.69399 9.98492 1.5 9 1.5ZM9 15C7.81332 15 6.65328 14.6481 5.66658 13.9888C4.67989 13.3295 3.91085 12.3925 3.45673 11.2961C3.0026 10.1997 2.88378 8.99334 3.11529 7.82946C3.3468 6.66557 3.91825 5.59647 4.75736 4.75736C5.59648 3.91824 6.66558 3.3468 7.82946 3.11529C8.99335 2.88378 10.1997 3.0026 11.2961 3.45672C12.3925 3.91085 13.3295 4.67988 13.9888 5.66658C14.6481 6.65327 15 7.81331 15 9C15 10.5913 14.3679 12.1174 13.2426 13.2426C12.1174 14.3679 10.5913 15 9 15Z'
                      fill='var(--alternative-text-color)'
                    />
                  </svg>
                </ModuleContentTooltip>
                {isInteractionDisabled ||
                (type === 'Products' &&
                  inputs.hsCode.length < 1 &&
                  inputs.desc.length < 1) ? (
                  <div className='screening-input-submit-group'>
                    <button className='btn-new-automate-disabled'>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        {isInteractionDisabled && <LockIcon fontSize='small' />}
                        Run Screen
                      </div>
                    </button>
                  </div>
                ) : (
                  <div className='screening-input-submit-group'>
                    <button
                      className='btn-new-automate'
                      onClick={() => runIfEnabled(() => handleSubmit(page))}
                    >
                      <div>Run Screen</div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {results.isLoading && !usesScreeningRedesign && (
        <div>
          <CircularProgress style={{ color: '#0E8BFF' }} />
        </div>
      )}
      {isProductSearch && !isRestricted && (
        <AIEvalProduct aiSums={aiSums} aiLoading={aiLoading} />
      )}
      {isEntitySearch && false && !isRestricted && (
        <>
          <div className='entity-screening-insight-grid'>
            <div className='entity-screening-risk-card'>
              <div className='entity-screening-card-heading'>
                <ShieldOutlinedIcon sx={{ fontSize: 20 }} />
                <span>Overall Risk Evaluation</span>
                <button
                  type='button'
                  className='risk-methodology-trigger'
                  onClick={openRiskModal}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                  Risk Matrix
                </button>
              </div>
              <div className={`entity-screening-risk-value ${entityRiskClass}`}>
                {aiLoading ? 'Evaluating' : entityRiskLabel}
              </div>
              {!aiLoading && latestAiSummary && entityCountryDataGap && (
                <div className='entity-screening-country-note warn'>
                  {entityCountryRisk?.flag ||
                    'Country unknown — verify jurisdiction'}
                </div>
              )}
              {!aiLoading &&
                latestAiSummary &&
                !entityCountryDataGap &&
                entityCountryDrove && (
                  <div
                    className={`entity-screening-country-note${
                      entityCountryRisk?.is_embargo ? ' warn' : ''
                    }`}
                  >
                    {entityCountryRisk?.is_embargo
                      ? `Comprehensive embargo${
                          entityCountryRisk?.iso
                            ? ` (${entityCountryRisk.iso})`
                            : ''
                        } — hard stop; escalate to compliance`
                      : entityCountryDriverNote}
                  </div>
                )}
              <p>
                {sanitizedExplanation ||
                  'Run a screening to generate a contextual risk evaluation for this entity.'}
              </p>
              <small>
                Based on {entityResultsTotal.toLocaleString()} screening
                results. The best {ENTITY_AI_SAMPLE_SIZE} matches from this
                search are sent to the AI evaluator.
              </small>
            </div>

            <Modal open={riskModalOpen} onClose={() => setRiskModalOpen(false)}>
              <div className='risk-methodology-modal'>
                <div className='risk-methodology-header'>
                  <span>How we evaluate risk</span>
                  <button
                    type='button'
                    aria-label='Close'
                    onClick={() => setRiskModalOpen(false)}
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
                <div className='risk-methodology-body'>
                  <p className='risk-methodology-intro'>
                    Overall risk is the <strong>highest</strong> (most severe)
                    of the signals below — a strong signal in any one of them
                    sets the rating.
                  </p>
                  <p className='risk-methodology-note'>
                    Confidence threshold: a hit is only sent to the AI evaluator
                    once its best-match confidence reaches{' '}
                    <strong>
                      {Math.round(
                        (riskConfig?.min_confidence_for_evaluation ?? 0.7) *
                          100,
                      )}
                      %
                    </strong>
                    ; anything below that is left for manual review.
                  </p>

                  <h4>Screening list matches</h4>
                  <ul className='risk-methodology-list'>
                    <li>
                      <span className='rm-tag high'>High</span> Sanctions,
                      export-control, and forced-labor lists (e.g. OFAC/SDN, BIS
                      Entity List, UFLPA).
                    </li>
                    <li>
                      <span className='rm-tag medium'>Medium</span> Regulatory
                      and enforcement lists, and commercial insights datasets.
                    </li>
                    <li>
                      <span className='rm-tag low'>Low</span> Informational,
                      NGO, and certification datasets.
                    </li>
                  </ul>

                  <h4>Country / jurisdiction risk</h4>
                  {riskConfigLoading && <p>Loading current configuration…</p>}
                  {!riskConfigLoading && !riskConfig && (
                    <p>
                      Configuration is unavailable right now. Please try again.
                    </p>
                  )}
                  {!riskConfigLoading && riskConfig && (
                    <>
                      <div className='risk-methodology-group'>
                        <div className='rm-group-title'>
                          <span className='rm-tag high'>High</span>
                          Comprehensive embargo — hard stop &amp; escalate
                        </div>
                        <div className='risk-country-chips'>
                          {riskConfig.embargo_countries.map((c) => (
                            <span key={c.code} className='risk-country-chip'>
                              {c.name}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className='risk-methodology-group'>
                        <div className='rm-group-title'>
                          <span className='rm-tag high'>High</span>
                          Broadly sanctioned jurisdictions
                        </div>
                        <div className='risk-country-chips'>
                          {riskConfig.high_risk_countries.map((c) => (
                            <span key={c.code} className='risk-country-chip'>
                              {c.name}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className='risk-methodology-group'>
                        <div className='rm-group-title'>
                          <span className='rm-tag medium'>Medium</span>
                          Transshipment / diversion hubs (risk floor)
                        </div>
                        <div className='risk-country-chips'>
                          {riskConfig.diversion_hubs.map((c) => (
                            <span key={c.code} className='risk-country-chip'>
                              {c.name}
                            </span>
                          ))}
                        </div>
                      </div>

                      {riskConfig.graded_tiers.length > 0 && (
                        <div className='risk-methodology-group'>
                          <div className='rm-group-title'>
                            Graded country tiers (AML / drugs / transshipment)
                          </div>
                          <div className='risk-country-chips'>
                            {riskConfig.graded_tiers.map((c) => (
                              <span key={c.code} className='risk-country-chip'>
                                {c.name}: {c.tier}
                                {c.category ? ` (${c.category})` : ''}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <p className='risk-methodology-note'>
                        An entity whose country is missing or unrecognized is
                        never auto-scored — it is flagged as a data gap so an
                        analyst can verify the jurisdiction.
                      </p>
                      {riskConfig.effective_date && (
                        <p className='risk-methodology-footer'>
                          Effective {riskConfig.effective_date}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Modal>

            <div
              className={`entity-screening-info-card${
                entityInsightPanels.ai ? ' active' : ''
              }`}
            >
              <div className='entity-screening-card-icon'>
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 22 }} />
              </div>
              <div>
                <h3>AI Evaluation</h3>
                <p>{aiLoading ? 'Generating now' : 'Generated from matches'}</p>
              </div>
              <span>
                AI analysis of risk factors and contextual match insights. The
                best {ENTITY_AI_SAMPLE_SIZE} matches from this search are sent
                to the AI evaluator.
              </span>
              <button
                type='button'
                onClick={() => {
                  if (!entityInsightPanels.ai) {
                    logAppAction('entity_screening:view_ai_evaluation');
                  }
                  toggleEntityInsightPanel('ai');
                }}
              >
                {entityInsightPanels.ai ? 'Hide Results' : 'View AI Evaluation'}
              </button>
            </div>

            <div
              className={`entity-screening-info-card${
                entityInsightPanels.media ? ' active' : ''
              }`}
            >
              <div className='entity-screening-card-icon'>
                <ArticleOutlinedIcon sx={{ fontSize: 22 }} />
              </div>
              <div>
                <h3>Adverse Media</h3>
                <p>
                  {isCheckingEntityAdverseMedia
                    ? 'Checking now'
                    : entityAdverseMedia
                    ? 'Checked'
                    : 'On-demand check'}
                </p>
              </div>
              <span>Check for negative news and media coverage</span>
              <button
                type='button'
                onClick={
                  entityAdverseMedia || entityAdverseMediaError
                    ? () => {
                        if (!entityInsightPanels.media) {
                          logAppAction('entity_screening:view_adverse_media');
                        }
                        toggleEntityInsightPanel('media');
                      }
                    : handleEntityAdverseMediaCheck
                }
                disabled={
                  isInteractionDisabled ||
                  isCheckingEntityAdverseMedia ||
                  !hasEntityQuery
                }
              >
                {isCheckingEntityAdverseMedia
                  ? 'Checking...'
                  : entityInsightPanels.media &&
                    (entityAdverseMedia || entityAdverseMediaError)
                  ? 'Hide Results'
                  : entityAdverseMedia || entityAdverseMediaError
                  ? 'View Results'
                  : 'Check Adverse Media'}
              </button>
            </div>

            <div
              className={`entity-screening-info-card${
                entityInsightPanels.ownership ? ' active' : ''
              }`}
            >
              <div className='entity-screening-card-icon'>
                <GroupsOutlinedIcon sx={{ fontSize: 22 }} />
              </div>
              <div>
                <h3>Beneficial Ownership</h3>
                <p>
                  {isCheckingEntityBeneficialOwnership
                    ? 'Checking now'
                    : entityBeneficialOwnership
                    ? 'Checked'
                    : '50% Rule Analysis'}
                </p>
              </div>
              <span>Identify ultimate beneficial owners when available</span>
              <button
                type='button'
                onClick={
                  entityBeneficialOwnership || entityBeneficialOwnershipError
                    ? () => {
                        if (!entityInsightPanels.ownership) {
                          logAppAction(
                            'entity_screening:view_beneficial_ownership',
                          );
                        }
                        toggleEntityInsightPanel('ownership');
                      }
                    : handleEntityBeneficialOwnershipCheck
                }
                disabled={
                  isInteractionDisabled ||
                  isCheckingEntityBeneficialOwnership ||
                  ownershipResults?.isLoading ||
                  !hasEntityQuery
                }
              >
                {isCheckingEntityBeneficialOwnership
                  ? 'Checking...'
                  : entityInsightPanels.ownership &&
                    (entityBeneficialOwnership ||
                      entityBeneficialOwnershipError)
                  ? 'Hide Results'
                  : entityBeneficialOwnership || entityBeneficialOwnershipError
                  ? 'View Results'
                  : 'Check Ownership'}
              </button>
            </div>
          </div>
          {(entityInsightPanels.ai ||
            entityInsightPanels.media ||
            entityInsightPanels.ownership) && (
            <div className='entity-screening-detail-stack'>
              {entityInsightPanels.ai && (
                <section className='entity-screening-detail-panel'>
                  <button
                    type='button'
                    className='entity-screening-detail-close'
                    onClick={() => toggleEntityInsightPanel('ai')}
                  >
                    x
                  </button>
                  <div className='entity-screening-detail-title'>
                    <span className='entity-screening-card-icon'>
                      <AutoAwesomeOutlinedIcon sx={{ fontSize: 22 }} />
                    </span>
                    <h3>AI Evaluation</h3>
                  </div>
                  <div className='entity-screening-detail-body'>
                    <p className='entity-screening-detail-summary'>
                      Based on the selected lenses and{' '}
                      {entityResultsTotal.toLocaleString()} screening results,
                      the entity profile shows{' '}
                      <strong className={entityRiskClass}>
                        {String(entityRiskLabel).toLowerCase()} risk exposure
                      </strong>
                      .{' '}
                      {sanitizedExplanation ||
                        'Run screening to generate a detailed risk narrative.'}
                    </p>
                    <div className='entity-screening-top-matches entity-screening-top-matches-card'>
                      <h4 className='screening-ai-sum-header-text'>
                        Top Matches
                      </h4>
                      {latestAiTopMatches.length > 0 ? (
                        <div className='screening-ai-sum-result-text'>
                          <ol>
                            {latestAiTopMatches
                              .slice(0, 5)
                              .map((match: string) => (
                                <li key={match}>
                                  <strong>{match}</strong>
                                </li>
                              ))}
                          </ol>
                        </div>
                      ) : (
                        <p>No top matches available for this evaluation.</p>
                      )}
                    </div>
                  </div>
                  <small>Generated from latest screening results</small>
                </section>
              )}

              {entityInsightPanels.media && (
                <section className='entity-screening-detail-panel'>
                  <button
                    type='button'
                    className='entity-screening-detail-close'
                    onClick={() => toggleEntityInsightPanel('media')}
                  >
                    x
                  </button>
                  <div className='entity-screening-detail-title'>
                    <span className='entity-screening-card-icon'>
                      <ArticleOutlinedIcon sx={{ fontSize: 22 }} />
                    </span>
                    <h3>Adverse Media</h3>
                  </div>
                  <p className='entity-screening-detail-summary'>
                    {entityAdverseMediaError
                      ? entityAdverseMediaError
                      : entityAdverseMedia?.summary ||
                        `Run an adverse media check for ${
                          getActiveEntityQuery() || 'this entity'
                        } to review negative news, enforcement, litigation, and other risk signals.`}
                  </p>
                  {entityAdverseMedia?.findings?.length ? (
                    <ul className='entity-screening-detail-list'>
                      {entityAdverseMedia.findings
                        .slice(0, 5)
                        .map((finding: any, idx: number) => {
                          const safeUrl = normalizeExternalUrl(finding.url);

                          return (
                            <li key={`${finding.url || finding.claim}-${idx}`}>
                              {safeUrl ? (
                                <a
                                  href={safeUrl}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                >
                                  {finding.claim || finding.source || 'Source'}
                                </a>
                              ) : (
                                finding.claim || finding.source || 'Finding'
                              )}
                              {finding.date ? ` - ${finding.date}` : ''}
                            </li>
                          );
                        })}
                    </ul>
                  ) : (
                    !entityAdverseMediaError && (
                      <button
                        type='button'
                        className='entity-screening-panel-action'
                        onClick={handleEntityAdverseMediaCheck}
                        disabled={
                          isInteractionDisabled ||
                          isCheckingEntityAdverseMedia ||
                          !hasEntityQuery
                        }
                      >
                        {isCheckingEntityAdverseMedia
                          ? 'Checking...'
                          : 'Check Adverse Media'}
                      </button>
                    )
                  )}
                </section>
              )}

              {entityInsightPanels.ownership && (
                <section className='entity-screening-detail-panel'>
                  <button
                    type='button'
                    className='entity-screening-detail-close'
                    onClick={() => toggleEntityInsightPanel('ownership')}
                  >
                    x
                  </button>
                  <div className='entity-screening-detail-title'>
                    <span className='entity-screening-card-icon'>
                      <GroupsOutlinedIcon sx={{ fontSize: 22 }} />
                    </span>
                    <h3>Beneficial Ownership</h3>
                  </div>
                  <div className='entity-screening-detail-body'>
                    <p className='entity-screening-detail-summary'>
                      {entityBeneficialOwnershipError
                        ? entityBeneficialOwnershipError
                        : entityBeneficialOwnership?.summary ||
                          `Run a beneficial ownership check for ${
                            getActiveEntityQuery() || 'this entity'
                          } to identify ownership and control signals.`}
                    </p>
                    {entityBeneficialOwnership?.top_matches?.length ? (
                      <div className='entity-screening-top-matches entity-screening-top-matches-card'>
                        <h4 className='screening-ai-sum-header-text'>
                          Top Matches
                        </h4>
                        <div className='screening-ai-sum-result-text'>
                          <ol>
                            {entityBeneficialOwnership.top_matches
                              .slice(0, 5)
                              .map((match: string, idx: number) => (
                                <li key={`${match}-${idx}`}>{match}</li>
                              ))}
                          </ol>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  {!entityBeneficialOwnership?.checked &&
                    !entityBeneficialOwnership?.summary &&
                    !entityBeneficialOwnershipError && (
                      <button
                        type='button'
                        className='entity-screening-panel-action'
                        onClick={handleEntityBeneficialOwnershipCheck}
                        disabled={
                          isInteractionDisabled ||
                          isCheckingEntityBeneficialOwnership ||
                          ownershipResults?.isLoading ||
                          !hasEntityQuery
                        }
                      >
                        {isCheckingEntityBeneficialOwnership
                          ? 'Checking...'
                          : 'Check Ownership'}
                      </button>
                    )}
                </section>
              )}
            </div>
          )}
        </>
      )}
      {isEntitySearch &&
        false &&
        (results.data.length > 0 || results.isLoading) && (
          <div className='entity-screening-results-heading'>
            <h2>Screening Results</h2>
            <p>
              Entity matches across selected lenses. Click any row to view match
              details.
            </p>
            {results?.truncated && (
              <p className='entity-screening-results-truncation'>
                Showing the first {loadedEntityCount.toLocaleString()} of{' '}
                {Number(results?.total || 0).toLocaleString()} matches — narrow
                your search or filters to see the rest.
              </p>
            )}
          </div>
        )}
      {isProductSearch && (
        <div className='entity-screening-results-heading'>
          <h2>Screening Results</h2>
          <p>
            Product matches across selected list and HTS filters. Click any row
            to view match details.
          </p>
        </div>
      )}
      {isEntitySearch &&
        false &&
        (results.data.length > 0 || results.isLoading) && (
          <div className='entity-screening-results-shell'>
            <EntityScreeningResultsTable
              key={`entity-results-table-${tableResetSignal}`}
              handleSubmit={handleSubmit}
              setPage={setPage}
              page={page}
              results={results}
              ownershipResults={ownershipResults}
              searchTerm={inputs.primary}
              entityFilterOptions={entityFilterOptions}
              isStatic={Boolean(results?.complete)}
            />
          </div>
        )}
      {isProductSearch && (
        <div className='entity-screening-results-shell product-screening-results-shell'>
          <ProductScreeningResultsTable
            handleSubmit={handleSubmit}
            setPage={setPage}
            page={page}
            results={results}
          />
        </div>
      )}
      {!results.isLoading &&
        !usesScreeningRedesign &&
        results.data.length === 1 &&
        (type === 'Cyber' ||
          !results.data[0]?.result?.body?.hits?.hits[0]?.matched_queries) && (
          <SingleScreeningResults
            setShowFullRecord={setShowFullRecord}
            type={type}
            handleSubmit={handleSubmit}
            setPage={setPage}
            page={page}
            results={results}
            cyberHeaders={cyberHeaders}
            getCSVResults={getCSVResults}
            setTicketNameOpen={setTicketNameOpen}
            inputs={inputs}
          />
        )}

      {!results.isLoading &&
        !usesScreeningRedesign &&
        results.data.length > 0 &&
        results.data[0]?.result?.body?.hits?.hits[0]?.matched_queries?.length >
          0 &&
        !(type === 'Cyber') && (
          <MultiScreeningResults
            setShowFullRecord={setShowFullRecord}
            type={type}
            handleSubmit={handleSubmit}
            inputs={inputs}
            setPage={setPage}
            page={page}
            results={results}
            cyberHeaders={cyberHeaders}
            handleMultiPageChange={handleMultiPageChange}
            createTicket={createTicket}
            saving={saving}
            shareEmail={shareEmail}
            setShareEmail={setShareEmail}
            setFrequency={setFrequency}
            frequency={frequency}
            ticketNameOpen={ticketNameOpen}
            setTicketNameOpen={setTicketNameOpen}
            newTicketName={newTicketName}
            setNewTicketName={setNewTicketName}
          />
        )}
      {isEntitySearch && (
        <SaveFilterModal
          open={saveFilterModalOpen}
          onClose={() => setSaveFilterModalOpen(false)}
          onSave={handleSaveFilter}
          summary={saveFilterSummary}
          existingNames={savedEntityFilters.map(
            (filter: any) => filter.filterName,
          )}
        />
      )}
      <Modal open={ticketNameOpen} onClose={handleTicketNameClose}>
        <CreateTicketWizard
          cur_user={cur_user}
          ticketType={'screening'}
          docType={wizardDocType}
          setFrequency={setFrequency}
          frequency={frequency}
          handleSaveTicket={createTicket}
          newTicketName={newTicketName}
          setNewTicketName={setNewTicketName}
          shareEmail={shareEmail}
          setShareEmail={setShareEmail}
          savingTicket={saving || false}
        />
      </Modal>
      <Modal
        open={entityExportFileNameOpen}
        onClose={handleEntityExportFileNameClose}
      >
        <div
          className='modal-cont'
          style={{ width: '480px', maxWidth: '90vw', padding: 0 }}
        >
          <div
            className='modal-header-cont-temp'
            style={{
              padding: '24px',
              borderBottom: '1px solid var(--es-soft-border)',
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                alignItems: 'flex-start',
              }}
            >
              <div className='modal-header-cont-text'>
                <h1
                  className='modal-header-cont-text-h1'
                  style={{ fontSize: '20px', marginBottom: '4px' }}
                >
                  Export Results
                </h1>
                <p className='modal-header-cont-text-p' style={{ margin: 0 }}>
                  Name the CSV file to download your screening results.
                </p>
              </div>
              <div
                className='module-header-btn-close'
                onClick={handleEntityExportFileNameClose}
                style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}
              >
                <CloseIcon />
              </div>
            </div>
          </div>

          <div
            className='modal-body'
            style={{
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <label
              style={{
                color: 'var(--text-primary-color)',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              File Name
            </label>
            <input
              type='text'
              value={entityExportFileName}
              autoFocus
              onChange={(e) => setEntityExportFileName(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  handleEntityExport();
                  handleEntityExportFileNameClose();
                }
              }}
              placeholder='e.g. entity_screening_results'
              style={{
                background: 'var(--bg-colors)',
                color: 'var(--text-primary-color)',
                border: '1px solid var(--es-soft-border)',
                padding: '12px 14px',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div
            style={{
              padding: '24px',
              borderTop: '1px solid var(--es-soft-border)',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <button
              className='btn-outline'
              onClick={handleEntityExportFileNameClose}
              style={{ margin: 0, width: 'auto' }}
            >
              Cancel
            </button>
            <button
              className='btn-primary'
              onClick={() => {
                handleEntityExport();
                handleEntityExportFileNameClose();
              }}
              style={{
                margin: 0,
                width: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
              Download
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Search;
